import {
  ArrowLeftOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PushpinOutlined,
  VideoCameraOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  BaseIndex,
  FaceDetector,
  FaceDetectorEvent,
  ImageProcessResult,
} from '@index5/face-detector';
import {
  Button,
  Empty,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Segmented,
  Space,
  Switch,
} from 'antd';
import App from 'antd/lib/app';
import 'chart.js';
import DOMPurify from 'dompurify';
import { TFunction } from 'i18next';
import mammoth from 'mammoth/mammoth.browser';
import * as PDFJS from 'pdfjs-dist/legacy/build/pdf.mjs';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import * as XLSX from 'xlsx';

import { API, TAssetLink } from '../api';
import { PageHeader } from '../components/common/page-header';
import {
  DOCUMENT_PLAYBACK_SPEEDS,
  DocumentPlaybackScrollMode,
  DocumentPlaybackSettings,
  DocumentPlaybackSpeed,
  DocumentProgressSnapshot,
  isDocumentProgressComplete,
  useDocumentPlayback,
} from '../components/document-viewer/use-document-playback';
import { BaseSettings } from '../components/form-items/base-settings';
import {
  BlockSelectable,
  TOption,
} from '../components/form-items/block-selectable';
import { SubtitlesArea } from '../components/form-items/subtitles';
import {
  SDK_API_HOST,
  SDK_INTEGRATE_TOKEN,
  SDK_PLAYER_SESSION,
} from '../constants';
import { ModeSettings } from '../constants/link-settings_by_mode';
import { LocaleKeys } from '../locale';
import { DarkTheme } from '../styles/theme';
import { Paths } from '../types/common';
import { TLinkForm } from '../types/responses/video';
import { EPlayerMode, ModeOptions } from '../types/video';
import {
  AttentionDecisionSample,
  resolveAttentionDecision,
} from '../utils/attention-decision';
import { aimanagerModule1Assets } from './aimanager-module1-data';
import { getAiManagerModule1DisplayTitle } from './aimanager-module1-display-titles';
import { AssetKind, ModuleAsset, moduleFolders } from './module1-data';

PDFJS.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  // @ts-expect-error Webpack resolves import.meta.url for this worker asset.
  import.meta.url
).toString();

const ASSET_LINK_LOAD_TIMEOUT_MS = 15000;
const FILE_LOAD_TIMEOUT_MS = 45000;
const isDebugVideoPlayerEnabled = () =>
  typeof window !== 'undefined' &&
  window.localStorage.debugVideoPlayer === 'true';
const ASSET_KINDS: AssetKind[] = [
  'video',
  'excel',
  'document',
  'pdf',
  'text',
  'csv',
  'file',
];

type ViewerState =
  | { status: 'idle' | 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      url: string;
      text?: string;
      html?: string;
      workbook?: XLSX.WorkBook;
      pdfData?: ArrayBuffer;
    };

type LiveDocumentIndexes = {
  attention: number;
  involvement: number;
  emotionality: number;
  distraction: number;
  tiredness: number;
  viewingSeconds: number;
};

const INITIAL_DOCUMENT_INDEXES: LiveDocumentIndexes = {
  attention: 0,
  involvement: 0,
  emotionality: 0,
  distraction: 0,
  tiredness: 0,
  viewingSeconds: 0,
};

const getDocumentPlaybackSpeedLabel = (value: DocumentPlaybackSpeed) =>
  String(DOCUMENT_PLAYBACK_SPEEDS.indexOf(value) + 1);

const formatDocumentResultDuration = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds - hours * 3600) / 60);
  const seconds = safeSeconds - hours * 3600 - minutes * 60;
  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours} hour`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (seconds > 0) parts.push(`${seconds} sec`);

  return parts.length ? parts.join(' ') : '0 sec';
};

const DOCUMENT_READING_WINDOW_LINES = 5;
const DOCUMENT_READING_LINE_HEIGHT = 24;
const DOCUMENT_READING_WINDOW_HEIGHT =
  DOCUMENT_READING_WINDOW_LINES * DOCUMENT_READING_LINE_HEIGHT;

const documentFaceIndexCanvas = document.createElement('canvas');
const FACE_INDEX_EVENT_MAX_AGE_MS = 8000;
const DOCUMENT_DECISION_ABSENT_ATTENTION = 5;
// Mirrors INDEX_AVERAGE_WINDOW_MS from the video player's reference pipeline
// (plai_player/src/powered/indexes-service.tsx) so measured indices behave
// identically between the video player and the file viewer.
const DOCUMENT_INDEX_AVERAGE_WINDOW_MS = 8000;
// Mirrors EMOTIONALITY_DECAY_HALF_LIFE_MS: emotionality decays smoothly
// toward 0 instead of dropping the instant no positive sample arrives.
const DOCUMENT_EMOTIONALITY_DECAY_HALF_LIFE_MS = 15000;
const DOCUMENT_FACE_INDEX_DEBUG_INTERVAL_MS = 10000;
const DOCUMENT_FACE_PROXY_DEBUG_HEADER = 'X-Debug-Face-SDK';
const DOCUMENT_FACE_PROXY_PLACEHOLDER_TOKEN = 'proxied-by-cloudflare';
const DOCUMENT_SECONDARY_INDEX_KEYS = [
  'involvement',
  'distraction',
  'tiredness',
  'emotionality',
] as const;
const DOCUMENT_ATTENTION_ABSENT_GRACE_MS = 2500;
const DOCUMENT_ATTENTION_ABSENT_PAUSE_SUSTAIN_MS = 3500;
const DOCUMENT_ATTENTION_GRACE_MS = 5000;
const DOCUMENT_ATTENTION_PAUSE_SUSTAIN_MS = 8000;

type TimedDocumentIndexSample = {
  receivedAt: number;
  values: LiveDocumentIndexes;
};

type DocumentEmotionalitySmoothingState = {
  updatedAt: number | null;
  value: number;
};

type DocumentFaceIndexSource = 'server' | 'sdk-base';

type DocumentFaceIndexDecisionSample = {
  rawIndexes: LiveDocumentIndexes;
  receivedAt: number;
  source: DocumentFaceIndexSource;
  hasSignal: boolean;
  hardAbsent: boolean;
  displayAverageReset: boolean;
};

const clampPercent = (value: number) =>
  Math.max(0, Math.min(100, Math.round(value)));
const normalizeIndexPercent = (value: number | null | undefined) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }

  return clampPercent(value <= 2 ? value * 100 : value);
};

const getFaceDocumentIndexPayload = (
  result: Partial<ImageProcessResult & BaseIndex> | undefined
) =>
  ((result as any)?.result ??
    (result as any)?.data?.result ??
    (result as any)?.indexes ??
    (result as any)?.data ??
    result) as Partial<ImageProcessResult & BaseIndex> | undefined;

const getObjectKeys = (value: unknown) =>
  value && typeof value === 'object' ? Object.keys(value) : [];

const isProxiedFaceSdkHost = (host: string | undefined) =>
  Boolean(
    host &&
      (host.startsWith('/api/face-detector') ||
        new URL(host, window.location.origin).pathname.startsWith(
          '/api/face-detector'
        ))
  );

const getFaceDebugRequestHeaders = (integrationToken: string) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [DOCUMENT_FACE_PROXY_DEBUG_HEADER]: 'true',
  };

  if (integrationToken) {
    headers['x-token'] = integrationToken;
  }

  return headers;
};

const getFaceProxyDebugHeaders = (response: Response) => ({
  proxy: response.headers.get('X-Face-SDK-Proxy'),
  targetHost: response.headers.get('X-Face-SDK-Target-Host'),
  targetPath: response.headers.get('X-Face-SDK-Target-Path'),
  tokenSource: response.headers.get('X-Face-SDK-Token-Source'),
  envToken: response.headers.get('X-Face-SDK-Env-Token'),
  upstreamStatus: response.headers.get('X-Face-SDK-Response-Status'),
});

const getDocumentVideoDiagnostics = (video: HTMLVideoElement | null) => {
  if (!video) {
    return null;
  }

  const stream = video.srcObject as MediaStream | null;

  return {
    readyState: video.readyState,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    paused: video.paused,
    ended: video.ended,
    muted: video.muted,
    currentTime: Math.round(video.currentTime * 1000) / 1000,
    streamActive: stream?.active ?? null,
    videoTracks:
      stream?.getVideoTracks().map((track) => ({
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
        label: track.label,
      })) ?? [],
  };
};

const decodeJwtPayload = (token: string | null) => {
  if (!token) {
    return null;
  }

  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(decoded) as Record<string, any>;
  } catch (error) {
    return null;
  }
};

const getDocumentViewerIdentity = () => {
  const payload = decodeJwtPayload(localStorage.getItem('plai-access'));
  const account = payload?.account ?? {};
  const userId =
    payload?.id ?? payload?.sub ?? account?.id ?? 'document-viewer';
  const username =
    account?.email ?? account?.firstName ?? payload?.email ?? 'Document viewer';

  return {
    userId: String(userId),
    username: String(username),
  };
};

const toFaceDocumentIndexes = (
  result: Partial<ImageProcessResult & BaseIndex> | undefined,
  startedAt: number | null
): LiveDocumentIndexes => {
  const payload = getFaceDocumentIndexPayload(result);

  return {
    attention: normalizeIndexPercent(payload?.attention),
    involvement: normalizeIndexPercent(payload?.involvement),
    emotionality: normalizeIndexPercent(
      (payload as any)?.emotions ?? (payload as any)?.happiness
    ),
    distraction: normalizeIndexPercent(
      (payload as any)?.distraction ?? (payload as any)?.amazement
    ),
    tiredness: normalizeIndexPercent((payload as any)?.tiredness),
    viewingSeconds: startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0,
  };
};

const hasDocumentIndexSignal = (values: LiveDocumentIndexes) =>
  values.attention > 0 ||
  values.involvement > 0 ||
  values.emotionality > 0 ||
  values.distraction > 0 ||
  values.tiredness > 0;

// Mean of the positive samples for a secondary index within the averaging
// window, falling back to the last known averaged value when nothing
// positive arrived — mirrors averagePositiveSecondaryIndex in
// indexes-service.tsx.
const averagePositiveSecondaryDocumentIndex = (
  samples: TimedDocumentIndexSample[],
  key: (typeof DOCUMENT_SECONDARY_INDEX_KEYS)[number],
  fallback: number
) => {
  const values = samples
    .map((sample) => sample.values[key])
    .filter((value) => value > 0);

  if (!values.length) return fallback;

  return Math.round(
    values.reduce((sum, value) => sum + value, 0) / values.length
  );
};

// Mirrors averageIndexSamples in indexes-service.tsx: attention is a plain
// mean over the sliding window, the secondary indices use the positive-
// sample average above, and emotionality is later overwritten by the decay
// smoothing applied in pushDocumentIndexSample below.
const averageDocumentIndexSamples = (
  samples: TimedDocumentIndexSample[],
  fallback: LiveDocumentIndexes,
  viewingSeconds: number
): LiveDocumentIndexes => {
  const attentionTotal = samples.reduce(
    (sum, sample) => sum + sample.values.attention,
    0
  );
  const count = Math.max(samples.length, 1);

  return {
    attention: Math.round(attentionTotal / count),
    involvement: averagePositiveSecondaryDocumentIndex(
      samples,
      'involvement',
      fallback.involvement
    ),
    distraction: averagePositiveSecondaryDocumentIndex(
      samples,
      'distraction',
      fallback.distraction
    ),
    tiredness: averagePositiveSecondaryDocumentIndex(
      samples,
      'tiredness',
      fallback.tiredness
    ),
    emotionality: averagePositiveSecondaryDocumentIndex(
      samples,
      'emotionality',
      0
    ),
    viewingSeconds,
  };
};

// Creates a stateful pusher scoped to a single measurement session, mirroring
// pushIndexData in indexes-service.tsx: it keeps its own sliding-window
// sample buffer and emotionality decay state so real (not fabricated)
// measured values behave identically to the video player.
const createDocumentIndexPusher = () => {
  let indexSamples: TimedDocumentIndexSample[] = [];
  let lastAveraged: LiveDocumentIndexes = INITIAL_DOCUMENT_INDEXES;
  let lastServerSample: TimedDocumentIndexSample | null = null;
  const emotionalitySmoothing: DocumentEmotionalitySmoothingState = {
    updatedAt: null,
    value: 0,
  };

  return (
    nextValues: LiveDocumentIndexes,
    isServerResult: boolean,
    options: { includeInAverage?: boolean; resetAverage?: boolean } = {}
  ): LiveDocumentIndexes => {
    const now = Date.now();
    const values: LiveDocumentIndexes = { ...nextValues };
    const recentServerValues =
      lastServerSample &&
      now - lastServerSample.receivedAt <= DOCUMENT_INDEX_AVERAGE_WINDOW_MS
        ? lastServerSample.values
        : null;

    if (isServerResult) {
      lastServerSample = {
        receivedAt: now,
        values,
      };
    } else if (recentServerValues) {
      for (const key of DOCUMENT_SECONDARY_INDEX_KEYS) {
        if (values[key] <= 0 && recentServerValues[key] > 0) {
          values[key] = recentServerValues[key];
        }
      }
    }

    if (options.resetAverage) {
      indexSamples = [];
      lastAveraged = {
        ...INITIAL_DOCUMENT_INDEXES,
        viewingSeconds: values.viewingSeconds,
      };
      emotionalitySmoothing.value = 0;
      emotionalitySmoothing.updatedAt = null;
    }

    const recentSamples = indexSamples.filter(
      (sample) => now - sample.receivedAt <= DOCUMENT_INDEX_AVERAGE_WINDOW_MS
    );
    if (options.includeInAverage === false) {
      indexSamples = recentSamples;
      lastAveraged = {
        ...lastAveraged,
        viewingSeconds: values.viewingSeconds,
      };

      return lastAveraged;
    }

    const samples = [...recentSamples, { receivedAt: now, values }];
    const averagedValues = averageDocumentIndexSamples(
      samples,
      lastAveraged,
      values.viewingSeconds
    );
    const previousEmotionality = emotionalitySmoothing.value;

    if (values.emotionality > 0) {
      emotionalitySmoothing.value = values.emotionality;
      emotionalitySmoothing.updatedAt = now;
    } else if (isServerResult) {
      const previousUpdatedAt = emotionalitySmoothing.updatedAt ?? now;
      const elapsedMs = Math.max(now - previousUpdatedAt, 0);
      const decayFactor = Math.pow(
        0.5,
        elapsedMs / DOCUMENT_EMOTIONALITY_DECAY_HALF_LIFE_MS
      );
      emotionalitySmoothing.value = previousEmotionality * decayFactor;
      emotionalitySmoothing.updatedAt = now;
    }

    averagedValues.emotionality = Math.round(emotionalitySmoothing.value);

    indexSamples = samples;
    lastAveraged = averagedValues;

    return averagedValues;
  };
};

const getAssetLinkAccessErrorMessage = (error: unknown) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as any).response?.status === 'number'
  ) {
    const status = (error as any).response.status;
    if (status === 401) {
      return 'Please sign in to open this parameterized file link.';
    }

    if (status === 403) {
      return 'Student role or higher is required to open this file and start index tracking.';
    }
  }

  return 'Parameterized link not found or unavailable.';
};

const findAssetByKey = (key: string | null): ModuleAsset | null => {
  if (!key) {
    return null;
  }

  for (const folder of moduleFolders) {
    const asset = folder.files.find((file) => file.s3Key === key);
    if (asset) {
      return asset;
    }
  }

  const aiManagerAsset = aimanagerModule1Assets.find(
    (file) => file.s3Key === key
  );
  if (aiManagerAsset) {
    return aiManagerAsset;
  }

  return null;
};

const getStreamUrl = (s3Key: string) =>
  `/api/streams/video-by-key?key=${encodeURIComponent(s3Key)}`;

const getStreamHeaders = () => {
  const token = localStorage.getItem('plai-access');

  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

const inferAssetKindFromName = (name: string): AssetKind => {
  const extension = name.split('.').pop()?.toLowerCase();

  if (extension === 'txt' || extension === 'md') return 'text';
  if (extension === 'csv') return 'csv';
  if (extension === 'xls' || extension === 'xlsx') return 'excel';
  if (extension === 'pdf') return 'pdf';
  if (extension === 'doc' || extension === 'docx') return 'document';
  if (
    extension === 'mp4' ||
    extension === 'webm' ||
    extension === 'mov' ||
    extension === 'mkv'
  ) {
    return 'video';
  }

  return 'file';
};

const normalizeAssetKind = (
  kind: string | null,
  fileName: string
): AssetKind =>
  ASSET_KINDS.includes(kind as AssetKind)
    ? (kind as AssetKind)
    : inferAssetKindFromName(fileName);

const isAbortLikeError = (error: unknown) =>
  (error instanceof DOMException && error.name === 'AbortError') ||
  (typeof error === 'object' &&
    error !== null &&
    ((error as { name?: string }).name === 'CanceledError' ||
      (error as { code?: string }).code === 'ERR_CANCELED'));

const getBackPath = (
  asset: ModuleAsset | null,
  fromParam: string | null,
  defaultModulePath = Paths.MODULE1
) => {
  if (
    fromParam === Paths.UPLOADED_VIDEO ||
    fromParam === Paths.UPLOADED_FILES ||
    fromParam === Paths.FILES ||
    fromParam === Paths.AIMANAGER_MODULE1
  ) {
    return fromParam;
  }

  if (asset?.kind === 'video') {
    return Paths.UPLOADED_VIDEO;
  }

  if (asset) {
    return Paths.FILES;
  }

  return defaultModulePath;
};

const getAssetLinkViewUrl = (
  assetLinkId: string,
  fromParam: string | null,
  viewPath: Paths = Paths.MODULE1_VIEW
) => {
  const params = new URLSearchParams({ assetLinkId });
  if (fromParam) {
    params.set('from', fromParam);
  }

  return `${viewPath}?${params.toString()}`;
};

const notifyQualifiedStudyComplete = () => {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: 'plai:qualified-complete' }, '*');
  }
};

const DefaultLinkSettings: TLinkForm = {
  viewingGuarantee: true,
  volumeLimit: false,
  volumeLimitValue: 0,
  increaseAttention: false,
  increaseAttentionValue: 0,
  rewindOption: false,
  distanceControl: false,
  videoSpeedControl: false,
  videoRecommendations: false,
  minimumAttentionLimit: false,
  minimumAttentionLimitValue: 0,
  attentionLimit: false,
  title: '',
  playerMode: EPlayerMode.CustomSettings,
  captionsEnabled: false,
  captions: '',
  documentAutoScroll: false,
  documentDefaultSpeed: 0.2,
  documentRequiredCoverage: 95,
  documentAttentionThreshold: 35,
};

const getModeOptions: (t: TFunction<string[]>) => TOption[] = (t) => [
  {
    label: t(ModeOptions[EPlayerMode.AdvertisingMarketing]),
    value: EPlayerMode.AdvertisingMarketing,
  },
  {
    label: t(ModeOptions[EPlayerMode.Training]),
    value: EPlayerMode.Training,
  },
  {
    label: t(ModeOptions[EPlayerMode.HealthSafety]),
    value: EPlayerMode.HealthSafety,
  },
  {
    label: t(ModeOptions[EPlayerMode.KidsMode]),
    value: EPlayerMode.KidsMode,
  },
  {
    label: t(ModeOptions[EPlayerMode.CustomSettings]),
    value: EPlayerMode.CustomSettings,
  },
];

const getArrayBuffer = async (s3Key: string, signal: AbortSignal) => {
  const response = await fetch(getStreamUrl(s3Key), {
    headers: getStreamHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Unable to load file (${response.status})`);
  }

  return response.arrayBuffer();
};

type ViewportFitProps = {
  $fitToViewport?: boolean;
  $embedded?: boolean;
};

type MobileDocumentControlsProps = ViewportFitProps & {
  $controlsVisible?: boolean;
  $controlsPinned?: boolean;
};

type DocumentScaleMode = 'fit' | 'read';

type DocumentViewportLayout = {
  height: number;
  isMobileLandscape: boolean;
  isMobilePortrait: boolean;
  width: number;
};

const getDocumentViewportLayout = (): DocumentViewportLayout => {
  if (typeof window === 'undefined') {
    return {
      height: 0,
      isMobileLandscape: false,
      isMobilePortrait: false,
      width: 0,
    };
  }

  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  const height =
    window.innerHeight || document.documentElement.clientHeight || 0;
  const coarsePointer =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  const isMobile = coarsePointer || width <= 720;

  return {
    height,
    isMobileLandscape: isMobile && width > height,
    isMobilePortrait: isMobile && height >= width,
    width,
  };
};

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const Shell = styled('section')<ViewportFitProps>`
  min-height: calc(100vh - 40px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  padding: ${({ $embedded }) =>
    $embedded ? '18px 16px 28px' : '28px 32px 52px'};
  color: hsl(218 28% 94%);
  background: hsl(220 28% 7%);

  ${({ $fitToViewport }) =>
    $fitToViewport
      ? `
        min-height: 0;
        overflow: hidden;
      `
      : ''}

  ${({ $fitToViewport, $embedded }) =>
    $fitToViewport
      ? `
        height: ${$embedded ? '100svh' : 'calc(100vh - 40px)'};
      `
      : ''}

  @media (max-width: 720px), (pointer: coarse) {
    padding: ${({ $embedded }) => ($embedded ? '0' : '18px 12px 36px')};
    min-height: ${({ $embedded }) => ($embedded ? '100svh' : 'auto')};
  }

  @supports not (height: 100svh) {
    ${({ $fitToViewport, $embedded }) =>
      $fitToViewport && $embedded
        ? `
          height: 100vh;
        `
        : ''}
  }
`;

const Content = styled('div')<ViewportFitProps>`
  width: 100%;
  max-width: ${({ $embedded }) => ($embedded ? 'none' : '1360px')};
  min-width: 0;
  margin: ${({ $embedded }) => ($embedded ? '0' : '0 auto')};
  box-sizing: border-box;

  ${({ $fitToViewport }) =>
    $fitToViewport
      ? `
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      `
      : ''}
`;

const PlayerStage = styled('div')<ViewportFitProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;

  ${({ $fitToViewport }) =>
    $fitToViewport
      ? `
        flex: 1 1 auto;
        min-height: 0;
      `
      : ''}

  @media (max-width: 720px), (pointer: coarse) {
    height: ${({ $embedded }) =>
      $embedded
        ? '100svh'
        : 'calc(100svh - 122px - env(safe-area-inset-bottom, 0px))'};
    min-height: ${({ $embedded }) => ($embedded ? '0' : '420px')};
    overflow: hidden;
    border-radius: ${({ $embedded }) => ($embedded ? '0' : '8px')};
  }

  @supports not (height: 100svh) {
    @media (max-width: 720px), (pointer: coarse) {
      height: ${({ $embedded }) =>
        $embedded
          ? '100vh'
          : 'calc(100vh - 122px - env(safe-area-inset-bottom, 0px))'};
    }
  }
`;

const Header = styled('div')`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  margin: 18px 0 22px;

  @media (max-width: 720px) {
    grid-template-columns: auto minmax(0, 1fr);
    margin: 8px 0 14px;
    padding: 0 10px;
  }
`;

const BackLink = styled(NavLink)`
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid hsl(216 18% 28%);
  border-radius: 8px;
  color: hsl(214 24% 78%);
  background: hsl(220 26% 10%);
  transition:
    border-color 0.18s ease,
    color 0.18s ease,
    background 0.18s ease;

  &:hover {
    border-color: hsl(188 82% 48%);
    color: hsl(188 92% 58%);
    background: hsl(220 26% 13%);
  }
`;

const TitleBlock = styled('div')`
  min-width: 0;
`;

const Title = styled('h1')`
  margin: 0;
  overflow: hidden;
  color: hsl(216 33% 97%);
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 720px) {
    font-size: 18px;
    white-space: normal;
  }
`;

const Meta = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  color: hsl(214 18% 62%);
  font-size: 13px;
`;

const Panel = styled('div')<ViewportFitProps>`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  min-height: 460px;
  border: 1px solid hsl(216 18% 21%);
  border-radius: 8px;
  background: hsl(220 26% 10%);

  ${({ $fitToViewport }) =>
    $fitToViewport
      ? `
        flex: 1 1 auto;
        height: clamp(460px, calc(100vh - 300px), 640px);
        min-height: 0;
      `
      : ''}

  @media (max-width: 720px), (pointer: coarse) {
    min-height: 360px;
    height: ${({ $fitToViewport }) => ($fitToViewport ? '100%' : 'auto')};
    border-right: ${({ $embedded }) =>
      $embedded ? '0' : '1px solid hsl(216 18% 21%)'};
    border-left: ${({ $embedded }) =>
      $embedded ? '0' : '1px solid hsl(216 18% 21%)'};
    border-radius: ${({ $embedded }) => ($embedded ? '0' : '8px')};
  }
`;

const HeaderActions = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin: -8px 0 18px;
`;

const AssetLinksPanel = styled('section')`
  display: grid;
  gap: 12px;
  margin: 0 0 22px;
`;

const AssetLinksTitle = styled('h2')`
  margin: 0;
  color: hsl(216 33% 97%);
  font-size: 18px;
  font-weight: 800;
`;

const AssetLinkRow = styled('div')`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid hsl(216 18% 21%);
  border-radius: 8px;
  background: hsl(220 26% 10%);

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const AssetLinkInfo = styled('div')`
  min-width: 0;
`;

const AssetLinkName = styled('div')`
  overflow: hidden;
  color: hsl(216 33% 97%);
  font-size: 15px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AssetLinkDescription = styled('div')`
  overflow: hidden;
  margin-top: 4px;
  color: hsl(214 18% 62%);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AssetLinkStats = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 9px;
`;

const AssetLinkStat = styled('span')`
  padding: 5px 9px;
  border-radius: 999px;
  color: hsl(188 88% 72%);
  font-size: 12px;
  font-weight: 700;
  background: hsl(185 88% 12% / 0.68);
`;

const AssetLinkActions = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 820px) {
    justify-content: flex-start;
  }
`;

const MeasurementBar = styled('div')<ViewportFitProps>`
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  margin: 0 0 14px;
  padding: 12px 14px;
  border: 1px solid hsl(188 82% 34% / 0.6);
  border-radius: 8px;
  background: hsl(220 28% 8% / 0.96);
  box-shadow: 0 10px 28px hsl(220 40% 2% / 0.35);

  ${({ $fitToViewport, $embedded }) =>
    $fitToViewport && $embedded
      ? `
        top: 24px;
        margin-bottom: 10px;
        padding: 10px 14px;
      `
      : ''}

  @media (max-width: 820px) {
    position: static;
    grid-template-columns: 1fr;
  }

  @media (max-width: 720px), (pointer: coarse) {
    ${({ $fitToViewport }) =>
      $fitToViewport
        ? `
          position: absolute;
          inset: 0;
          z-index: 5;
          display: block;
          width: 100%;
          margin: 0;
          padding: 0;
          overflow: visible;
          border: 0;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
          pointer-events: none;
        `
        : `
          margin: 0 10px 12px;
          width: calc(100% - 20px);
          padding: 10px;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
  }
`;

const FileQualifiedBadge = styled('div')<ViewportFitProps>`
  position: absolute;
  top: 6px;
  left: 18px;
  z-index: 7;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px;
  border: 1px solid hsl(188 82% 54% / 0.42);
  border-radius: 7px;
  color: hsl(184 96% 76%);
  background: hsl(185 88% 12% / 0.2);
  box-shadow: 0 0 14px hsl(188 82% 54% / 0.14);
  font-size: 9px;
  font-weight: 400;
  line-height: 1;
  pointer-events: none;

  & + ${MeasurementBar} {
    margin-top: 28px;
  }

  .anticon {
    color: hsl(176 72% 53%);
    font-size: 10px;
  }

  @media (max-width: 720px), (pointer: coarse) {
    top: calc(8px + env(safe-area-inset-top, 0px));
    left: 14px;
    padding: 2px 5px;
    font-size: 6px;

    .anticon {
      font-size: 7px;
    }

    & + ${MeasurementBar} {
      margin-top: 0;
    }
  }
`;

const MeasurementItems = styled('div')`
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  gap: 8px;

  @media (max-width: 720px), (pointer: coarse) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 4px;
    scrollbar-width: thin;
    scrollbar-color: hsl(188 82% 34% / 0.8) transparent;
    -webkit-overflow-scrolling: touch;
  }
`;

const MeasurementItem = styled('div')`
  min-width: 132px;
  padding: 8px 10px;
  border-radius: 8px;
  background: hsl(218 30% 14%);

  @media (max-width: 720px), (pointer: coarse) {
    flex: 0 0 clamp(92px, 29vw, 118px);
    min-width: 0;
    padding: 7px 8px;
  }
`;

const MeasurementLabel = styled('div')`
  overflow: hidden;
  color: hsl(214 18% 62%);
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MeasurementValue = styled('div')`
  margin-top: 3px;
  color: hsl(188 88% 72%);
  font-size: 18px;
  font-weight: 900;

  @media (max-width: 720px), (pointer: coarse) {
    font-size: 16px;
  }
`;

const IndexItems = styled('div')<MobileDocumentControlsProps>`
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  gap: 12px;
  width: 100%;
  justify-content: center;

  @media (max-width: 720px), (pointer: coarse) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    justify-content: flex-start;
    padding-bottom: 4px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }

    ${({ $fitToViewport }) =>
      $fitToViewport
        ? `
          position: absolute;
          top: calc(34px + env(safe-area-inset-top, 0px));
          right: 18px;
          left: 18px;
          width: auto;
          padding: 5px 8px;
          border: 1px solid hsl(188 82% 34% / 0.44);
          border-radius: 16px;
          background: hsl(220 30% 4% / 0.58);
          backdrop-filter: blur(12px);
          box-shadow: 0 10px 24px hsl(220 40% 2% / 0.38);
        `
        : ''}

    ${({ $fitToViewport, $controlsPinned, $controlsVisible }) =>
      $fitToViewport
        ? `
          opacity: ${$controlsPinned || $controlsVisible ? 1 : 0};
          pointer-events: ${
            $controlsPinned || $controlsVisible ? 'auto' : 'none'
          };
          transform: translateY(${
            $controlsPinned || $controlsVisible ? '0' : 'calc(-100% - 24px)'
          });
          transition:
            opacity 0.22s ease,
            transform 0.28s ease;
        `
        : ''}
  }
`;

const IndexItem = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 60px;

  @media (max-width: 720px) {
    flex: unset;

    ${IndexItems} & {
      flex: 0 0 50px;
      min-width: 50px;
      gap: 2px;
    }
  }
`;

const IndexValueWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 30px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 20px;
  padding-inline: 14px;

  @media (max-width: 720px) {
    width: 60px;
    height: 26px;
    padding-inline: 8px;

    ${IndexItems} & {
      width: 42px;
      height: 17px;
      padding-inline: 4px;
    }
  }
`;

const IndexValue = styled('span')`
  color: #39d5c9;
  font-weight: bold;
  font-size: 14px;

  @media (max-width: 720px) {
    font-size: 12px;

    ${IndexItems} & {
      font-size: 8px;
    }
  }
`;

const IndexLabel = styled('div')`
  color: hsl(218 28% 94%);
  font-weight: 500;
  font-size: 14px;
  font-family: Inter;
  line-height: 14px;
  text-align: center;

  @media (max-width: 720px) {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 60px;
    display: block;
    font-size: 10px;

    ${IndexItems} & {
      min-width: 0;
      max-width: 50px;
      font-size: 5px;
      line-height: 7px;
    }
  }
`;

const PlaybackControls = styled('div')<MobileDocumentControlsProps>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;
  gap: 10px;
  align-items: center;

  .play-pause-btn,
  .scale-btn,
  .pin-btn {
    background: transparent;
    border: 1px solid hsl(188 82% 34% / 0.6);
    color: #39d5c9;

    &:hover,
    &:focus {
      color: #2abfb4;
      border-color: #39d5c9;
    }
  }

  .pin-btn.is-active {
    background: hsl(176 72% 53% / 0.16);
    border-color: hsl(176 72% 53% / 0.95);
    color: hsl(184 96% 76%);
    box-shadow: 0 0 14px hsl(176 72% 53% / 0.22);
  }

  @media (max-width: 720px), (pointer: coarse) {
    justify-content: center;
    flex-wrap: nowrap;
    width: 100%;
    gap: 12px;

    ${({ $fitToViewport }) =>
      $fitToViewport
        ? `
          position: absolute;
          right: 10px;
          bottom: calc(16px + env(safe-area-inset-bottom, 0px));
          left: 10px;
          display: grid;
          grid-template-columns: 34px 34px 34px minmax(116px, 1fr);
          grid-template-areas:
            "progress progress progress progress"
            "play scale pin speed";
          width: auto;
          min-height: 70px;
          padding: 7px 9px 8px;
          border: 1px solid hsl(188 82% 34% / 0.48);
          border-radius: 22px;
          background: hsl(220 30% 4% / 0.62);
          backdrop-filter: blur(12px);
          box-shadow: 0 -10px 24px hsl(220 40% 2% / 0.4);
        `
        : ''}

    ${({ $fitToViewport, $controlsPinned, $controlsVisible }) =>
      $fitToViewport
        ? `
          opacity: ${$controlsPinned || $controlsVisible ? 1 : 0};
          pointer-events: ${
            $controlsPinned || $controlsVisible ? 'auto' : 'none'
          };
          transform: translateY(${
            $controlsPinned || $controlsVisible ? '0' : 'calc(100% + 28px)'
          });
          transition:
            opacity 0.2s ease,
            transform 0.24s ease;
        `
        : ''}

    .play-pause-btn,
    .scale-btn,
    .pin-btn {
      padding: 0;
      width: 34px;
      height: 34px;
      min-width: 34px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      .btn-text {
        display: none;
      }
      .anticon {
        font-size: 20px;
        margin: 0;
      }
    }

    .play-pause-btn {
      grid-area: play;
    }

    .scale-btn {
      grid-area: scale;
    }

    .pin-btn {
      grid-area: pin;
    }

    .ant-segmented {
      grid-area: speed;
      font-size: 12px;
      justify-self: end;
      max-width: 100%;
      flex-shrink: 0;
      .ant-segmented-item-label {
        padding: 3px 7px;
      }
    }
  }
`;

const PlaybackProgress = styled('div')`
  width: clamp(220px, 26vw, 360px);
  min-width: 220px;

  .ant-progress-bg {
    background: hsl(176 64% 53%) !important;
  }

  @media (max-width: 720px), (pointer: coarse) {
    grid-area: progress;
    flex: 1;
    min-width: 0;
    width: 100%;

    ${PlaybackControls} & {
      max-width: none;
    }
  }
`;

const LinkSettings = styled('section')`
  display: grid;
  gap: 72px;
  max-width: 960px;
  margin: 18px 0 42px;
`;

const LinkSettingsRow = styled('div')`
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const LinkSettingsLabel = styled('span')`
  padding-top: 8px;
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  font-size: 14px;
  font-weight: 700;
`;

const LinkSettingsContent = styled('div')`
  width: 100%;
`;

const DocumentSettingsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 24px;
  padding: 18px;
  border: 1px solid hsl(216 18% 24%);
  border-radius: 8px;
  background: hsl(220 26% 10%);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentSetting = styled('label')`
  display: grid;
  gap: 8px;
  color: hsl(214 18% 72%);
  font-size: 13px;
  font-weight: 700;
`;

const GeneratedLink = styled('div')`
  overflow-wrap: anywhere;
  padding: 12px 14px;
  border: 1px solid hsl(216 18% 24%);
  border-radius: 7px;
  color: hsl(188 88% 72%);
  font:
    13px/1.5 Consolas,
    Monaco,
    'Courier New',
    monospace;
  background: hsl(220 28% 7%);
`;

const CenterMessage = styled('div')`
  display: grid;
  place-items: center;
  min-height: 460px;
  padding: 32px;
  color: hsl(214 18% 68%);
  text-align: center;
`;

const TextViewer = styled('pre')`
  margin: 0;
  padding: 24px;
  color: hsl(218 28% 94%);
  font:
    14px/1.6 Consolas,
    Monaco,
    'Courier New',
    monospace;
  white-space: pre-wrap;
`;

const HtmlViewer = styled('article')<ViewportFitProps>`
  width: 100%;
  max-width: ${({ $embedded }) => ($embedded ? 'none' : '960px')};
  min-height: 460px;
  margin: ${({ $embedded }) => ($embedded ? '0' : '0 auto')};
  padding: ${({ $embedded }) => ($embedded ? '36px 48px' : '36px')};
  color: hsl(222 24% 12%);
  background: white;

  h1,
  h2,
  h3 {
    color: hsl(222 24% 10%);
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td,
  th {
    padding: 6px 8px;
    border: 1px solid hsl(220 14% 84%);
  }
`;

const SheetWrap = styled('div')`
  width: 100%;
  min-width: 0;
  padding-bottom: 28px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const DocumentViewport = styled('div')<
  ViewportFitProps & { $qualified?: boolean }
>`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: auto;
  max-height: calc(100vh - 210px);
  min-height: 460px;
  height: 620px;
  outline: ${({ $qualified }) =>
    $qualified ? '2px solid hsl(176 72% 53% / 0.9)' : '0 solid transparent'};
  outline-offset: -2px;
  scroll-behavior: auto;

  ${({ $fitToViewport }) =>
    $fitToViewport
      ? `
        height: 100%;
        max-height: none;
        min-height: 0;
      `
      : ''}

  @media (max-width: 720px), (pointer: coarse) {
    ${({ $fitToViewport }) =>
      $fitToViewport
        ? `
          height: 100%;
          max-height: none;
          min-height: 0;
          padding-top: calc(104px + env(safe-area-inset-top, 0px));
          padding-bottom: calc(104px + env(safe-area-inset-bottom, 0px));
        `
        : ''}
  }
`;

const DocumentStage = styled('div')`
  position: relative;
  height: 100%;
  max-width: 100%;
  min-height: 460px;
  overflow: hidden;

  @media (max-width: 720px), (pointer: coarse) {
    min-height: 360px;
    height: 100%;
  }
`;

const DocumentScaleFrame = styled('div')`
  position: relative;
  min-width: 100%;
  min-height: 100%;
`;

const DocumentScaleInner = styled('div')<{ $scale: number }>`
  display: inline-block;
  min-width: 100%;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: left top;
`;

const DocumentFocusOverlay = styled('div')<{
  $visible: boolean;
  $windowBottomPx: number;
}>`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  top: ${({ $windowBottomPx }) =>
    `${Math.max(0, Math.round($windowBottomPx))}px`};
  z-index: 3;
  border-top: 1px solid hsl(188 82% 54% / 0.34);
  background: hsl(220 32% 5% / 0.72);
  backdrop-filter: blur(1.2px);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 0.18s ease,
    top 0.18s linear;
`;

const DocumentResultModal = styled(Modal)`
  .ant-modal-content {
    max-width: 450px;
    max-height: 492px;
    background-color: ${({ theme }) => theme.COLORS.BLACK._400};
    border-radius: 20px;
    padding: 30px;
  }

  .ant-modal-close {
    display: none;
  }

  @media (max-width: 720px), (pointer: coarse) {
    .ant-modal-content {
      max-width: 340px;
      max-height: 492px;
    }
  }
`;

const DocumentResultTitle = styled('div')`
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 20px;
  line-height: 24px;
  text-align: left;
  margin-bottom: 30px;
`;

const DocumentResultList = styled('ul')`
  padding: 0;
  list-style: none;
  margin: 0 0 30px;
`;

const DocumentResultListTitle = styled('div')`
  margin-bottom: 10px;
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  font-size: 18px;
  font-weight: 400;
  line-height: 23px;
  text-align: left;
`;

const DocumentResultListElement = styled('li')`
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  max-height: 18px;
  margin-bottom: 10px;

  &::before {
    display: inline-block;
    content: '';
    border-radius: 0.375rem;
    height: 8px;
    width: 8px;
    margin-right: 10px;
    background-color: ${({ theme }) => theme.COLORS.ACCENT._100};
  }
`;

const DocumentResultListValue = styled('span')`
  display: inline;
  padding-left: 10px;
  color: ${({ theme }) => theme.COLORS.ACCENT._100};
`;

const DocumentResultButtons = styled('div')`
  display: flex;
  gap: 10px;
`;

const DocumentResultButton = styled(Button)`
  background: ${({ theme }) => theme.COLORS.ACCENT._100};
  padding: 20px 20px 24px 20px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 500;
  line-height: 16px;

  &:first-of-type {
    padding: 20px 60px 24px 60px;

    @media (max-width: 720px), (pointer: coarse) {
      padding: 16px 18px 18px 18px;
      font-size: 14px;
      font-weight: 500;
      line-height: 14px;
      text-align: left;
    }
  }

  @media (max-width: 720px), (pointer: coarse) {
    padding: 16px 20px 18px 20px;
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
    text-align: left;
  }
`;

const SheetTitle = styled('h2')`
  margin: 18px 20px 10px;
  color: hsl(218 28% 94%);
  font-size: 17px;
`;

const SheetTable = styled('table')`
  width: 100%;
  min-width: min(1120px, 100%);
  table-layout: fixed;
  border-collapse: collapse;
  color: hsl(218 28% 94%);

  td,
  th {
    min-width: 180px;
    padding: 9px 12px;
    border: 1px solid hsl(216 18% 21%);
    vertical-align: top;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  tr:first-of-type td {
    color: hsl(188 88% 70%);
    font-weight: 800;
    background: hsl(218 30% 14%);
  }

  @media (max-width: 720px), (pointer: coarse) {
    width: max-content;
    min-width: 920px;
    table-layout: auto;

    td,
    th {
      min-width: 160px;
      overflow-wrap: normal;
      word-break: normal;
    }
  }
`;

const Video = styled('video')`
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  max-height: calc(100vh - 210px);
  background: black;
`;

const HiddenCameraVideo = styled('video')`
  position: fixed;
  right: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const PdfPages = styled('div')`
  display: grid;
  justify-items: center;
  gap: 18px;
  min-width: max-content;
  padding: 18px;
  background: hsl(220 20% 18%);
`;

const PdfCanvas = styled('canvas')`
  display: block;
  max-width: calc(100vw - 110px);
  height: auto;
  background: white;
  box-shadow: 0 2px 12px hsl(220 30% 3% / 0.45);

  @media (max-width: 720px) {
    max-width: calc(100vw - 48px);
  }
`;

const PdfDocument: FC<{ data: ArrayBuffer; onReady: () => void }> = ({
  data,
  onReady,
}) => {
  const [document, setDocument] = useState<any>(null);
  const [error, setError] = useState('');
  const canvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({});

  useEffect(() => {
    let active = true;
    const task = PDFJS.getDocument({ data: new Uint8Array(data.slice(0)) });

    void task.promise
      .then((nextDocument) => {
        if (active) {
          setDocument(nextDocument);
          setError('');
        } else {
          void nextDocument.destroy();
        }
      })
      .catch(() => {
        if (active) {
          setError('Unable to render PDF.');
        }
      });

    return () => {
      active = false;
      void task.destroy();
    };
  }, [data]);

  useEffect(() => {
    if (!document) {
      return;
    }

    let active = true;
    const renderTasks: { cancel: () => void }[] = [];

    const renderPages = async () => {
      for (
        let pageNumber = 1;
        pageNumber <= document.numPages;
        pageNumber += 1
      ) {
        const page = await document.getPage(pageNumber);
        if (!active) {
          return;
        }

        const canvas = canvasRefs.current[pageNumber];
        const context = canvas?.getContext('2d');
        if (!canvas || !context) {
          continue;
        }

        const viewport = page.getViewport({ scale: 1.35 });
        const outputScale = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const renderTask = page.render({
          canvasContext: context,
          viewport,
          transform:
            outputScale === 1
              ? undefined
              : [outputScale, 0, 0, outputScale, 0, 0],
        });
        renderTasks.push(renderTask);
        await renderTask.promise;
      }

      if (active) {
        onReady();
      }
    };

    void renderPages().catch(() => {
      if (active) {
        setError('Unable to render PDF.');
      }
    });

    return () => {
      active = false;
      renderTasks.forEach((task) => task.cancel());
    };
  }, [document, onReady]);

  if (error) {
    return <CenterMessage>{error}</CenterMessage>;
  }

  if (!document) {
    return <CenterMessage>Rendering PDF...</CenterMessage>;
  }

  return (
    <PdfPages>
      {Array.from({ length: document.numPages }, (_, index) => {
        const pageNumber = index + 1;
        return (
          <PdfCanvas
            key={pageNumber}
            ref={(canvas) => {
              canvasRefs.current[pageNumber] = canvas;
            }}
            data-document-section={`pdf-page-${pageNumber}`}
          />
        );
      })}
    </PdfPages>
  );
};

const WorkbookViewer: FC<{ workbook: XLSX.WorkBook }> = ({ workbook }) => {
  return (
    <SheetWrap>
      {workbook.SheetNames.map((sheetName) => {
        const rows = XLSX.utils.sheet_to_json<string[]>(
          workbook.Sheets[sheetName],
          { header: 1, raw: false, blankrows: false }
        );

        return (
          <section key={sheetName} data-document-section={sheetName}>
            <SheetTitle>{sheetName}</SheetTitle>
            <SheetTable>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </SheetTable>
          </section>
        );
      })}
    </SheetWrap>
  );
};

const AssetLinksList: FC<{
  links: TAssetLink[];
  loading: boolean;
  onDelete: (id: string) => void;
  fromPath: string;
  viewPath: Paths;
}> = ({ links, loading, onDelete, fromPath, viewPath }) => {
  const copyLink = useCallback(
    async (id: string) => {
      const link = `${window.location.origin}${getAssetLinkViewUrl(
        id,
        fromPath,
        viewPath
      )}`;
      await navigator.clipboard.writeText(link);
    },
    [fromPath, viewPath]
  );

  if (loading) {
    return (
      <AssetLinksPanel>
        <AssetLinksTitle>Parameterized file links</AssetLinksTitle>
        <CenterMessage>Loading links...</CenterMessage>
      </AssetLinksPanel>
    );
  }

  return (
    <AssetLinksPanel>
      <AssetLinksTitle>Parameterized file links</AssetLinksTitle>
      {!links.length ? (
        <Empty description="No parameterized links for this file yet" />
      ) : (
        links.map((link, index) => (
          <AssetLinkRow key={link.id}>
            <AssetLinkInfo>
              <AssetLinkName>
                Link {index + 1} / {link.title || link.name}
              </AssetLinkName>
              <AssetLinkDescription>
                {link.description || 'No description'}
              </AssetLinkDescription>
              <AssetLinkStats>
                <AssetLinkStat>{link.viewsCount ?? 0} views</AssetLinkStat>
                <AssetLinkStat>
                  {link.settings?.increaseAttentionValue ?? 0}% attention
                </AssetLinkStat>
                <AssetLinkStat>
                  {link.settings?.minimumAttentionLimitValue ?? 0}% involvement
                </AssetLinkStat>
              </AssetLinkStats>
            </AssetLinkInfo>
            <AssetLinkActions>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  void copyLink(link.id);
                }}
              >
                Copy
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => {
                  window.location.href = `/analytics?assetLinkId=${link.id}`;
                }}
              >
                Stats
              </Button>
              <Popconfirm
                title="Delete file link?"
                description="This parameterized link will stop opening for viewers."
                okButtonProps={{ danger: true }}
                onConfirm={() => onDelete(link.id)}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </AssetLinkActions>
          </AssetLinkRow>
        ))
      )}
    </AssetLinksPanel>
  );
};

const LinkCreator: FC<{
  asset: ModuleAsset;
  fromParam: string | null;
  title: string;
  description: string;
  generatedLink: string;
  viewPath: Paths;
  setTitle: (value: string) => void;
  setDescription: (value: string) => void;
  setGeneratedLink: (value: string) => void;
  onCreated: (link: TAssetLink) => void;
  onSuccess: (link: string) => void;
  onError: () => void;
}> = ({
  asset,
  fromParam,
  title,
  description,
  generatedLink,
  viewPath,
  setTitle,
  setDescription,
  setGeneratedLink,
  onCreated,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation([LocaleKeys.VIDEO]);
  const [isCreating, setIsCreating] = useState(false);
  const [mode, setMode] = useState<EPlayerMode>(EPlayerMode.CustomSettings);
  const [settings, setSettings] = useState<TLinkForm>(DefaultLinkSettings);
  const modeOptions = useMemo(() => getModeOptions(t), [t]);

  const changeMode = useCallback((value: EPlayerMode) => {
    setMode(value);
    setSettings((prev) => ({
      ...DefaultLinkSettings,
      ...prev,
      ...ModeSettings[value],
      playerMode: value,
      title: prev.title,
    }));
  }, []);

  const onBaseSettingsChange = useCallback(
    (formKey: string, formValue: any) => {
      setSettings((prev) => ({
        ...prev,
        [formKey]: formValue,
        ...(formKey === 'increaseAttentionValue'
          ? { documentAttentionThreshold: formValue }
          : {}),
      }));
    },
    []
  );

  const handleCreate = useCallback(async () => {
    if (!title.trim()) {
      onError();
      return;
    }

    setIsCreating(true);
    try {
      const response = await API.assetLinks.create({
        s3Key: asset.s3Key,
        name: asset.name,
        title: title.trim(),
        description: description.trim(),
        settings: {
          ...settings,
          title: title.trim(),
          playerMode: mode,
        },
        kind: asset.kind,
        size: asset.size,
        isAuthRequired: false,
      });
      const path = getAssetLinkViewUrl(response.data.id, fromParam, viewPath);
      const link = `${window.location.origin}${path}`;
      setGeneratedLink(link);
      onCreated(response.data);
      onSuccess(link);
    } catch (error) {
      onError();
    } finally {
      setIsCreating(false);
    }
  }, [
    asset.kind,
    asset.name,
    asset.s3Key,
    asset.size,
    description,
    fromParam,
    mode,
    onError,
    onCreated,
    onSuccess,
    setGeneratedLink,
    settings,
    title,
    viewPath,
  ]);

  return (
    <LinkSettings>
      <LinkSettingsRow>
        <LinkSettingsLabel>{t('settings.general')}</LinkSettingsLabel>
        <LinkSettingsContent>
          <SubtitlesArea
            isInput
            text={title}
            setText={setTitle}
            placeholder={t('settings.link.title')}
          />
          <SubtitlesArea
            text={description}
            setText={setDescription}
            placeholder={t('settings.link.description')}
          />
        </LinkSettingsContent>
      </LinkSettingsRow>
      <LinkSettingsRow>
        <LinkSettingsLabel>{t('settings.player.title')}</LinkSettingsLabel>
        <LinkSettingsContent>
          <BlockSelectable
            options={modeOptions}
            value={mode}
            setValue={changeMode as any}
          />
        </LinkSettingsContent>
      </LinkSettingsRow>
      <LinkSettingsRow>
        <LinkSettingsLabel>{t('settings.title')}</LinkSettingsLabel>
        <LinkSettingsContent>
          <BaseSettings
            settings={settings}
            onBaseSettingsChange={onBaseSettingsChange}
          />
        </LinkSettingsContent>
      </LinkSettingsRow>
      {asset.kind !== 'video' && (
        <LinkSettingsRow>
          <LinkSettingsLabel>Document</LinkSettingsLabel>
          <LinkSettingsContent>
            <DocumentSettingsGrid>
              <DocumentSetting>
                Automatic scrolling
                <Switch
                  checked={settings.documentAutoScroll === true}
                  onChange={(value) =>
                    onBaseSettingsChange('documentAutoScroll', value)
                  }
                />
              </DocumentSetting>
              <DocumentSetting>
                Default speed
                <Segmented
                  block
                  options={DOCUMENT_PLAYBACK_SPEEDS.map((value) => ({
                    label: getDocumentPlaybackSpeedLabel(value),
                    value,
                  }))}
                  value={settings.documentDefaultSpeed ?? 0.2}
                  onChange={(value) =>
                    onBaseSettingsChange('documentDefaultSpeed', value)
                  }
                />
              </DocumentSetting>
              <DocumentSetting>
                Required coverage, %
                <InputNumber
                  min={50}
                  max={100}
                  value={settings.documentRequiredCoverage ?? 95}
                  onChange={(value) =>
                    onBaseSettingsChange(
                      'documentRequiredCoverage',
                      value ?? 95
                    )
                  }
                />
              </DocumentSetting>
              <DocumentSetting>
                Attention threshold, %
                <InputNumber
                  min={1}
                  max={100}
                  value={settings.documentAttentionThreshold ?? 35}
                  onChange={(value) =>
                    onBaseSettingsChange(
                      'documentAttentionThreshold',
                      value ?? 35
                    )
                  }
                />
              </DocumentSetting>
            </DocumentSettingsGrid>
          </LinkSettingsContent>
        </LinkSettingsRow>
      )}
      {generatedLink && <GeneratedLink>{generatedLink}</GeneratedLink>}
      <LinkSettingsRow>
        <LinkSettingsLabel />
        <LinkSettingsContent>
          <Space>
            <NavLink
              to={`${viewPath}?key=${encodeURIComponent(
                asset.s3Key
              )}&from=${encodeURIComponent(fromParam ?? Paths.FILES)}`}
            >
              <Button size="large">{t('buttons.cancel')}</Button>
            </NavLink>
            <Button size="large" loading={isCreating} onClick={handleCreate}>
              {t('buttons.link.create')}
            </Button>
            {generatedLink && (
              <Button
                size="large"
                onClick={() => navigator.clipboard.writeText(generatedLink)}
              >
                Copy link
              </Button>
            )}
          </Space>
        </LinkSettingsContent>
      </LinkSettingsRow>
    </LinkSettings>
  );
};

// Additive server-process heartbeat for the document tracker, mirroring the
// video player's dual signal source (plai_player/src/powered/indexes-service.tsx:
// SDK events PLUS a polled /api/v1/process heartbeat) so a lapse in SDK
// events alone can't starve the attention watchdog of signal.
const DOCUMENT_SERVER_PROCESS_INTERVAL_MS = 2500;
const DOCUMENT_SERVER_PROCESS_CANVAS_WIDTH = 640;

const getDocumentViewportOrientationAngle = () => {
  const screenOrientation = window.screen?.orientation;

  if (typeof screenOrientation?.angle === 'number') {
    return screenOrientation.angle;
  }

  const legacyOrientation = (window as unknown as { orientation?: number })
    .orientation;

  return typeof legacyOrientation === 'number' ? legacyOrientation : 0;
};

const getDocumentCameraFrameRotation = () => {
  const coarsePointer =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  const isLandscapeViewport = window.innerWidth > window.innerHeight;

  if (!coarsePointer || !isLandscapeViewport) return 0;

  const normalizedAngle =
    ((getDocumentViewportOrientationAngle() % 360) + 360) % 360;

  if (normalizedAngle === 90) return -90;
  if (normalizedAngle === 270) return 90;

  return 0;
};

// Adapted from indexes-service.tsx's drawCameraFrameToCanvas: draws a fresh
// (non-SDK-owned) canvas each capture, so it never races with the
// FaceDetector SDK's own rendering onto documentFaceIndexCanvas.
const drawDocumentCameraFrameToCanvas = (
  video: HTMLVideoElement,
  width: number,
  rotation = getDocumentCameraFrameRotation()
) => {
  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  const rotated = Math.abs(rotation) === 90;
  const height = Math.max(
    1,
    Math.round(
      width *
        (rotated ? sourceWidth / sourceHeight : sourceHeight / sourceWidth)
    )
  );
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });

  canvas.width = width;
  canvas.height = height;

  if (!context) return canvas;

  if (rotation === 90) {
    context.translate(width, 0);
    context.rotate(Math.PI / 2);
    context.drawImage(video, 0, 0, height, width);
  } else if (rotation === -90) {
    context.translate(0, height);
    context.rotate(-Math.PI / 2);
    context.drawImage(video, 0, 0, height, width);
  } else {
    context.drawImage(video, 0, 0, width, height);
  }

  return canvas;
};

const getDocumentServerImageFromVideo = (video: HTMLVideoElement) => {
  const canvas = drawDocumentCameraFrameToCanvas(
    video,
    DOCUMENT_SERVER_PROCESS_CANVAS_WIDTH
  );

  return canvas
    .toDataURL('image/jpeg', 0.5)
    .replace('data:image/jpeg;base64,', '');
};

const registerDocumentFaceSession = async (integrationToken: string) => {
  const headers = getFaceDebugRequestHeaders(integrationToken);

  console.info('[DocumentFaceIndex] Registering session:', {
    apiHost: SDK_API_HOST,
    sessionId: SDK_PLAYER_SESSION,
    usesProxy: isProxiedFaceSdkHost(SDK_API_HOST),
    hasClientToken: Boolean(SDK_INTEGRATE_TOKEN),
    sendsTokenHeader: Boolean(integrationToken),
  });

  const response = await fetch(`${SDK_API_HOST}/api/v1/sessions/register`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      external_session: SDK_PLAYER_SESSION,
    }),
  });
  const payload = await response.json().catch(() => null);
  const proxyDebug = getFaceProxyDebugHeaders(response);

  console.info('[DocumentFaceIndex] Session register response:', {
    httpStatus: response.status,
    ok: response.ok,
    proxy: proxyDebug,
    payloadStatus: payload?.status,
    payloadKeys: getObjectKeys(payload),
    detail: payload?.detail,
  });

  if (!response.ok) {
    throw new Error(
      `[DocumentFaceIndex] Session registration failed: ${response.status}`
    );
  }

  console.info('[DocumentFaceIndex] Session registered:', {
    status: payload?.status,
    hasPayload: Boolean(payload),
  });
};

const DocumentFaceIndexTracker: FC<{
  enabled: boolean;
  startedAt: number | null;
  linkId: string;
  contentId: string;
  fileType: string;
  viewSessionId: string;
  onIndexes: (
    indexes: LiveDocumentIndexes,
    decisionSample: DocumentFaceIndexDecisionSample
  ) => void;
  onStatusChange: (status: 'running' | 'fallback') => void;
}> = ({
  enabled,
  startedAt,
  linkId,
  contentId,
  fileType,
  viewSessionId,
  onIndexes,
  onStatusChange,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastSdkIndexAtRef = useRef(0);
  const lastDebugLogAtRef = useRef(0);
  const hasAcceptedIndexRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const usesProxy = isProxiedFaceSdkHost(SDK_API_HOST);
    if (!SDK_API_HOST || (!usesProxy && !SDK_INTEGRATE_TOKEN)) {
      onStatusChange('fallback');
      return;
    }

    const integrationToken =
      SDK_INTEGRATE_TOKEN ||
      (usesProxy ? DOCUMENT_FACE_PROXY_PLACEHOLDER_TOKEN : '');
    lastSdkIndexAtRef.current = 0;
    lastDebugLogAtRef.current = 0;
    hasAcceptedIndexRef.current = false;
    let active = true;
    let stream: MediaStream | null = null;
    let detector: any;
    let serverProcessingIntervalId: number | null = null;
    const pushIndexSample = createDocumentIndexPusher();
    let hardAbsentActive = false;

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera API is not available.');
        }

        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
        });

        if (!active || !videoRef.current) {
          return;
        }

        const video = videoRef.current;
        video.srcObject = stream;
        await video.play().catch(() => undefined);

        console.info('[DocumentFaceIndex] Camera stream ready:', {
          apiHost: SDK_API_HOST,
          usesProxy,
          hasClientToken: Boolean(SDK_INTEGRATE_TOKEN),
          sendsTokenHeader: Boolean(integrationToken),
          video: getDocumentVideoDiagnostics(video),
        });

        await registerDocumentFaceSession(integrationToken);

        const identity = getDocumentViewerIdentity();
        detector = new FaceDetector({
          apiUrl: SDK_API_HOST,
          sessionId: SDK_PLAYER_SESSION,
          // processServerFrame owns server requests; SDK supplies local indexes.
          mode: 'client',
          drawResults: false,
          video,
          canvas: documentFaceIndexCanvas,
          processPayload: {
            username: identity.username,
            user_id: identity.userId,
            link_id: linkId,
            video_id: '',
            content_id: contentId,
            file_type: fileType,
            view_session_id: viewSessionId,
            camera: 'enabled',
            microphone: false,
            eye_tracking: false,
            response_mode: 'blocking',
          } as any,
          integration_token: integrationToken,
        });

        const handleIndex = (
          result: Partial<ImageProcessResult & BaseIndex> | undefined,
          isServerResult: boolean
        ) => {
          if (!active) {
            return;
          }

          const now = Date.now();
          const values = toFaceDocumentIndexes(result, startedAt);
          const hasSignal = hasDocumentIndexSignal(values);
          const hardAbsent =
            !hasSignal ||
            values.attention <= DOCUMENT_DECISION_ABSENT_ATTENTION;
          const displayAverageReset = hardAbsentActive && !hardAbsent;
          const source: DocumentFaceIndexSource = isServerResult
            ? 'server'
            : 'sdk-base';

          if (!isServerResult && now - lastSdkIndexAtRef.current < 750) {
            return;
          }

          if (!isServerResult) {
            lastSdkIndexAtRef.current = now;
          }

          hardAbsentActive = hardAbsent;
          const displayValues = pushIndexSample(values, isServerResult, {
            includeInAverage: !hardAbsent,
            resetAverage: displayAverageReset,
          });
          const shouldLogDebug =
            !hasAcceptedIndexRef.current ||
            now - lastDebugLogAtRef.current >
              DOCUMENT_FACE_INDEX_DEBUG_INTERVAL_MS;

          if (shouldLogDebug) {
            const payload = getFaceDocumentIndexPayload(result);
            lastDebugLogAtRef.current = now;
            console.info('[DocumentFaceIndex] Index sample', {
              source,
              hasNestedResult: Boolean((result as any)?.result),
              rawKeys: getObjectKeys(result),
              payloadKeys: getObjectKeys(payload),
              rawValues: values,
              displayValues,
              hasSignal,
              hardAbsent,
              displayAverageReset,
            });
          }

          if (!hasAcceptedIndexRef.current) {
            hasAcceptedIndexRef.current = true;
            onStatusChange('running');
          }

          onIndexes(displayValues, {
            rawIndexes: values,
            receivedAt: now,
            source,
            hasSignal,
            hardAbsent,
            displayAverageReset,
          });
        };

        await detector
          .on(FaceDetectorEvent.Created, (mode: any) => {
            console.info('[DocumentFaceIndex] Detector created:', { mode });
          })
          .on(FaceDetectorEvent.SettingsUpdated, (settings: any) => {
            console.info('[DocumentFaceIndex] Detector settings updated:', {
              indexesCalculation: settings?.indexes_calculation,
              interval: settings?.interval,
            });
          })
          .on(FaceDetectorEvent.Error, (error: any) => {
            console.error('[DocumentFaceIndex] Detector SDK error:', error);
          })
          .on(FaceDetectorEvent.ImageProcessed, (result: any) =>
            handleIndex(result, true)
          )
          .on(FaceDetectorEvent.BaseIndexReceived, (result: any) =>
            handleIndex(result, false)
          )
          .create();

        if (active) {
          // Sole server sender, alongside the SDK's local detector.
          const processServerFrame = async () => {
            const currentVideo = videoRef.current;
            if (
              !active ||
              !currentVideo ||
              !currentVideo.videoWidth ||
              !currentVideo.videoHeight
            ) {
              return;
            }

            try {
              const headers = getFaceDebugRequestHeaders(integrationToken);
              const imageBlob = getDocumentServerImageFromVideo(currentVideo);
              const requestDiagnostics = {
                apiHost: SDK_API_HOST,
                sessionId: SDK_PLAYER_SESSION,
                hasClientToken: Boolean(SDK_INTEGRATE_TOKEN),
                sendsTokenHeader: Boolean(integrationToken),
                imageBlobLength: imageBlob.length,
                linkId,
                contentId,
                fileType,
                viewSessionId,
                video: getDocumentVideoDiagnostics(currentVideo),
              };

              console.info(
                '[DocumentFaceIndex] Server heartbeat request:',
                requestDiagnostics
              );

              const response = await fetch(
                `${SDK_API_HOST}/api/v1/process?external_session=${SDK_PLAYER_SESSION}`,
                {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    img_blob: imageBlob,
                    username: identity.username,
                    user_id: identity.userId,
                    link_id: linkId,
                    video_id: '',
                    content_id: contentId,
                    file_type: fileType,
                    view_session_id: viewSessionId,
                    camera: 'enabled',
                    microphone: false,
                    eye_tracking: false,
                    response_mode: 'blocking',
                  }),
                }
              );
              const payload = await response.json().catch(() => null);
              const proxyDebug = getFaceProxyDebugHeaders(response);
              const normalizedResult = toFaceDocumentIndexes(
                payload?.result,
                startedAt
              );

              console.info('[DocumentFaceIndex] Server heartbeat response:', {
                httpStatus: response.status,
                ok: response.ok,
                proxy: proxyDebug,
                status: payload?.status,
                payloadKeys: getObjectKeys(payload),
                resultKeys: getObjectKeys(payload?.result),
                nestedResultKeys: getObjectKeys(
                  (payload?.result as any)?.result
                ),
                normalizedResult,
              });

              if (!response.ok || payload?.status !== 'OK') {
                console.warn(
                  '[DocumentFaceIndex] Server heartbeat returned non-OK status:',
                  {
                    status: payload?.status,
                    httpStatus: response.status,
                    proxy: proxyDebug,
                    resultKeys: getObjectKeys(payload?.result),
                    normalizedResult,
                    detail: payload?.detail,
                    video: getDocumentVideoDiagnostics(currentVideo),
                  }
                );
                return;
              }

              handleIndex(payload.result, true);
            } catch (error) {
              console.error(
                '[DocumentFaceIndex] Server heartbeat error:',
                error
              );
            }
          };

          serverProcessingIntervalId = window.setInterval(
            processServerFrame,
            DOCUMENT_SERVER_PROCESS_INTERVAL_MS
          );
          void processServerFrame();
        }
      } catch (error) {
        if (active) {
          console.error('[DocumentFaceIndex] Tracker startup failed:', error);
          onStatusChange('fallback');
        }
      }
    };

    void start();

    return () => {
      active = false;
      if (serverProcessingIntervalId !== null) {
        window.clearInterval(serverProcessingIntervalId);
        serverProcessingIntervalId = null;
      }
      if (detector) {
        try {
          detector.destroy?.();
          detector.stop?.();
        } catch (error) {
          // Best-effort cleanup; media tracks are stopped below.
        }
      }
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [
    contentId,
    enabled,
    fileType,
    linkId,
    onIndexes,
    onStatusChange,
    startedAt,
    viewSessionId,
  ]);

  if (!enabled) {
    return null;
  }

  return <HiddenCameraVideo ref={videoRef} autoPlay muted playsInline />;
};

export const Module1ViewerPage: FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const key = searchParams.get('key');
  const queryKind = searchParams.get('kind');
  const queryName = searchParams.get('name');
  const querySize = searchParams.get('size');
  const assetLinkId = searchParams.get('assetLinkId');
  const fromParam = searchParams.get('from');
  const isEmbedded = searchParams.get('embedded') === '1';
  const safeFromParam = isEmbedded ? null : fromParam;
  const mode = searchParams.get('mode');
  const isCreateLinkMode = mode === 'create-link';
  const { message } = App.useApp();
  const isAiManagerModule = location.pathname.startsWith(
    Paths.AIMANAGER_MODULE1
  );
  const moduleViewPath = isAiManagerModule
    ? Paths.AIMANAGER_MODULE1_VIEW
    : Paths.MODULE1_VIEW;
  const defaultModulePath = isAiManagerModule
    ? Paths.AIMANAGER_MODULE1
    : Paths.MODULE1;
  const fallbackAsset = useMemo<ModuleAsset | null>(() => {
    if (!key) return null;

    const name = queryName?.trim() || key.split('/').pop() || 'File viewer';
    const kind = normalizeAssetKind(queryKind, name);

    return {
      kind,
      name: getAiManagerModule1DisplayTitle(key, name),
      size: querySize ?? '',
      s3Key: key,
    };
  }, [key, queryKind, queryName, querySize]);
  const manifestAsset = useMemo(
    () => findAssetByKey(key) ?? fallbackAsset,
    [fallbackAsset, key]
  );
  const [linkedAsset, setLinkedAsset] = useState<ModuleAsset | null>(null);
  const [activeAssetLink, setActiveAssetLink] = useState<TAssetLink | null>(
    null
  );
  const [isAssetLinkLoading, setIsAssetLinkLoading] = useState(false);
  const asset = linkedAsset ?? manifestAsset;
  const backPath = useMemo(
    () => getBackPath(asset, safeFromParam, defaultModulePath),
    [asset, defaultModulePath, safeFromParam]
  );
  const [state, setState] = useState<ViewerState>({ status: 'idle' });
  const viewStartedAtRef = useRef<number | null>(null);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [assetLinks, setAssetLinks] = useState<TAssetLink[]>([]);
  const [isAssetLinksLoading, setIsAssetLinksLoading] = useState(false);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isDocumentSummaryOpen, setDocumentSummaryOpen] = useState(false);
  const [liveIndexes, setLiveIndexes] = useState<LiveDocumentIndexes>(
    INITIAL_DOCUMENT_INDEXES
  );
  const [documentDecisionAttention, setDocumentDecisionAttention] = useState(0);
  const [documentDecisionSignalAt, setDocumentDecisionSignalAt] = useState<
    number | null
  >(null);
  const [documentDecisionHardAbsent, setDocumentDecisionHardAbsent] =
    useState(false);
  const [documentScaleMode, setDocumentScaleMode] =
    useState<DocumentScaleMode>('fit');
  const [documentViewportLayout, setDocumentViewportLayout] = useState(
    getDocumentViewportLayout
  );
  const [documentScale, setDocumentScale] = useState(1);
  const [documentNaturalSize, setDocumentNaturalSize] = useState({
    height: 0,
    width: 0,
  });
  const [documentControlsVisible, setDocumentControlsVisible] = useState(true);
  const [documentControlsPinned, setDocumentControlsPinned] = useState(false);
  const lastActivityAtRef = useRef(Date.now());
  const documentContentRef = useRef<HTMLDivElement | null>(null);
  const documentControlsHideTimerRef = useRef<number | null>(null);
  const liveIndexesRef = useRef(INITIAL_DOCUMENT_INDEXES);
  const documentCompletedRef = useRef(false);
  const lastDocumentDecisionBridgeLogAtRef = useRef(0);
  const lastDocumentDecisionBridgeStateRef = useRef('');
  const completedDocumentSnapshotRef = useRef<DocumentProgressSnapshot | null>(
    null
  );
  const completedDocumentIndexesRef = useRef<LiveDocumentIndexes | null>(null);
  const completedDocumentViewingSecondsRef = useRef<number | null>(null);
  const documentPauseCountRef = useRef(0);
  const completedDocumentPauseCountRef = useRef(0);
  const isQualifiedStudyCompleted =
    searchParams.get('qualified') === '1' || documentCompletedRef.current;
  const lastFaceIndexAtRef = useRef<number | null>(null);
  const lastServerDecisionSampleRef =
    useRef<DocumentFaceIndexDecisionSample | null>(null);
  const [faceIndexStatus, setFaceIndexStatus] = useState<
    'idle' | 'running' | 'fallback'
  >('idle');
  const faceIndexStatusRef = useRef<'idle' | 'running' | 'fallback'>('idle');
  const documentPlaybackSettings = useMemo<DocumentPlaybackSettings>(() => {
    const settings = activeAssetLink?.settings;
    const configuredSpeed = settings?.documentDefaultSpeed;
    const isMobileDocumentViewport =
      documentViewportLayout.isMobilePortrait ||
      documentViewportLayout.isMobileLandscape;
    const documentDefaultSpeed: DocumentPlaybackSpeed = isMobileDocumentViewport
      ? 0.2
      : configuredSpeed === 0.2 ||
          configuredSpeed === 0.5 ||
          configuredSpeed === 1 ||
          configuredSpeed === 2
        ? configuredSpeed
        : configuredSpeed === 1.5 || configuredSpeed === 3
          ? 2
          : 0.2;

    return {
      viewingGuarantee: settings?.viewingGuarantee === true,
      documentAutoScroll: settings?.documentAutoScroll === true,
      documentDefaultSpeed,
      documentRequiredCoverage: settings?.documentRequiredCoverage ?? 95,
      documentMinimumQualifiedDurationMs:
        settings?.documentMinimumQualifiedDurationMs ?? 0,
      documentAttentionThreshold:
        settings?.increaseAttention === true
          ? (settings?.increaseAttentionValue ?? 35)
          : 0,
    };
  }, [
    activeAssetLink?.settings,
    documentViewportLayout.isMobileLandscape,
    documentViewportLayout.isMobilePortrait,
  ]);
  const documentScrollMode: DocumentPlaybackScrollMode =
    documentViewportLayout.isMobilePortrait && documentScaleMode === 'read'
      ? 'zPath'
      : 'vertical';
  const documentReadingWindowHeightPx =
    documentViewportLayout.isMobilePortrait ||
    documentViewportLayout.isMobileLandscape
      ? 0
      : DOCUMENT_READING_WINDOW_HEIGHT;
  const documentPlayback = useDocumentPlayback({
    enabled: Boolean(
      assetLinkId &&
        state.status === 'ready' &&
        isTracking &&
        asset?.kind !== 'video' &&
        (asset?.kind !== 'pdf' || isPdfReady)
    ),
    contentKey: `${assetLinkId ?? 'preview'}:${asset?.s3Key ?? 'none'}:${
      state.status
    }:${isPdfReady}`,
    faceIndexStatus,
    attention: liveIndexes.attention,
    decisionAttention: documentDecisionAttention,
    decisionSignalAt: documentDecisionSignalAt,
    decisionHardAbsent: documentDecisionHardAbsent,
    settings: documentPlaybackSettings,
    allowUnfocused: true,
    readingWindowHeightPx: documentReadingWindowHeightPx,
    debug: isDebugVideoPlayerEnabled(),
    scrollMode: documentScrollMode,
  });

  useEffect(() => {
    if (!assetLinkId || asset?.kind === 'video' || state.status !== 'ready') {
      return;
    }

    if (!isDebugVideoPlayerEnabled()) {
      return;
    }

    console.info('[DocumentPlaybackDebug] Effective document settings:', {
      assetKind: asset?.kind,
      assetLinkId,
      attentionThreshold: documentPlaybackSettings.documentAttentionThreshold,
      documentAutoScroll: documentPlaybackSettings.documentAutoScroll,
      faceIndexStatus,
      isPlaying: documentPlayback.isPlaying,
      isTracking,
      pauseReason: documentPlayback.snapshot.pauseReason,
      viewingGuarantee: documentPlaybackSettings.viewingGuarantee,
    });
  }, [
    asset?.kind,
    assetLinkId,
    documentPlayback.isPlaying,
    documentPlayback.snapshot.pauseReason,
    documentPlaybackSettings.documentAttentionThreshold,
    documentPlaybackSettings.documentAutoScroll,
    documentPlaybackSettings.viewingGuarantee,
    faceIndexStatus,
    isTracking,
    state.status,
  ]);

  useEffect(() => {
    liveIndexesRef.current = liveIndexes;
  }, [liveIndexes]);

  useEffect(() => {
    const updateLayout = () => {
      setDocumentViewportLayout(getDocumentViewportLayout());
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    window.addEventListener('orientationchange', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('orientationchange', updateLayout);
    };
  }, []);

  useEffect(() => {
    setDocumentScaleMode('fit');
    setDocumentScale(1);
    setDocumentNaturalSize({ height: 0, width: 0 });
  }, [asset?.s3Key, assetLinkId]);

  const updateDocumentScale = useCallback(() => {
    const viewport = documentPlayback.viewportRef.current;
    const content = documentContentRef.current;

    if (!viewport || !content || asset?.kind === 'video') {
      setDocumentScale(1);
      setDocumentNaturalSize({ height: 0, width: 0 });
      return;
    }

    const naturalWidth = Math.max(content.scrollWidth, content.offsetWidth, 1);
    const naturalHeight = Math.max(
      content.scrollHeight,
      content.offsetHeight,
      1
    );
    const availableWidth = Math.max(viewport.clientWidth, 1);
    const availableHeight = Math.max(viewport.clientHeight, 1);
    let nextScale = 1;

    if (
      documentViewportLayout.isMobilePortrait &&
      documentScaleMode === 'fit'
    ) {
      nextScale = clampNumber(
        Math.min(
          availableWidth / naturalWidth,
          availableHeight / naturalHeight
        ),
        0.08,
        1
      );
    } else if (documentViewportLayout.isMobileLandscape) {
      nextScale = clampNumber(availableWidth / naturalWidth, 0.18, 1.15);
    }

    setDocumentNaturalSize({ height: naturalHeight, width: naturalWidth });
    setDocumentScale((current) =>
      Math.abs(current - nextScale) < 0.01 ? current : nextScale
    );
  }, [
    asset?.kind,
    documentPlayback.viewportRef,
    documentScaleMode,
    documentViewportLayout.isMobileLandscape,
    documentViewportLayout.isMobilePortrait,
  ]);

  useEffect(() => {
    if (asset?.kind === 'video' || state.status !== 'ready') {
      return undefined;
    }

    const viewport = documentPlayback.viewportRef.current;
    const content = documentContentRef.current;
    if (!viewport || !content) {
      return undefined;
    }

    let frameId = 0;
    const scheduleMeasure = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(updateDocumentScale);
    };

    scheduleMeasure();

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleMeasure)
        : null;
    observer?.observe(viewport);
    observer?.observe(content);
    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('orientationchange', scheduleMeasure);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      observer?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('orientationchange', scheduleMeasure);
    };
  }, [
    asset?.kind,
    documentPlayback.viewportRef,
    documentScaleMode,
    state.status,
    updateDocumentScale,
  ]);

  useEffect(() => {
    const viewport = documentPlayback.viewportRef.current;
    if (!viewport || asset?.kind === 'video') return;

    viewport.scrollTop = 0;
    viewport.scrollLeft = 0;
  }, [
    asset?.kind,
    asset?.s3Key,
    documentPlayback.viewportRef,
    documentScaleMode,
  ]);

  useEffect(() => {
    documentCompletedRef.current = false;
    completedDocumentSnapshotRef.current = null;
    completedDocumentIndexesRef.current = null;
    completedDocumentViewingSecondsRef.current = null;
    documentPauseCountRef.current = 0;
    completedDocumentPauseCountRef.current = 0;
    lastDocumentDecisionBridgeLogAtRef.current = 0;
    lastDocumentDecisionBridgeStateRef.current = '';
    lastServerDecisionSampleRef.current = null;
    setDocumentDecisionAttention(0);
    setDocumentDecisionSignalAt(null);
    setDocumentDecisionHardAbsent(false);
    setDocumentSummaryOpen(false);
  }, [assetLinkId, asset?.s3Key]);

  const handleFaceIndexes = useCallback(
    (
      indexes: LiveDocumentIndexes,
      decisionSample: DocumentFaceIndexDecisionSample
    ) => {
      // Real measured values only — a zero reading (e.g. no face detected)
      // is a legitimate watchdog measurement, but it should not poison the
      // slower display average.
      const now = Date.now();
      if (decisionSample.source === 'server') {
        lastServerDecisionSampleRef.current = decisionSample;
      }

      const lastServerDecisionSample = lastServerDecisionSampleRef.current;
      const serverDecisionAgeMs = lastServerDecisionSample
        ? now - lastServerDecisionSample.receivedAt
        : Number.POSITIVE_INFINITY;
      const serverAttentionSample: AttentionDecisionSample<LiveDocumentIndexes> | null =
        lastServerDecisionSample
          ? {
              receivedAt: lastServerDecisionSample.receivedAt,
              source: 'server',
              values: lastServerDecisionSample.rawIndexes,
            }
          : null;
      const rawAttentionSample: AttentionDecisionSample<LiveDocumentIndexes> = {
        receivedAt: decisionSample.receivedAt,
        source: decisionSample.source,
        values: decisionSample.rawIndexes,
      };
      const documentAttentionThreshold =
        documentPlaybackSettings.documentAttentionThreshold;
      const isAttentionPaused =
        documentPlayback.snapshot.pauseReason === 'attention-absent' ||
        documentPlayback.snapshot.pauseReason === 'attention-low';
      const attentionDecision = resolveAttentionDecision({
        now,
        serverSample: serverAttentionSample,
        rawSample: rawAttentionSample,
        averagedAttention: indexes.attention,
        firstIndexAdded: true,
        isPlaybackBuffering: false,
        lastHighAttentionAt: now,
        serverFreshMs: DOCUMENT_SERVER_PROCESS_INTERVAL_MS * 3,
        rawFreshMs: FACE_INDEX_EVENT_MAX_AGE_MS,
        increaseAttentionValue: documentAttentionThreshold,
        reductionAttentionValue: documentAttentionThreshold,
        absentAttentionValue: DOCUMENT_DECISION_ABSENT_ATTENTION,
        lowAttentionGraceMs: DOCUMENT_ATTENTION_GRACE_MS,
        absentAttentionGraceMs: DOCUMENT_ATTENTION_ABSENT_GRACE_MS,
        lowAttentionSustainMs: DOCUMENT_ATTENTION_PAUSE_SUSTAIN_MS,
        absentAttentionSustainMs: DOCUMENT_ATTENTION_ABSENT_PAUSE_SUSTAIN_MS,
        suppressRawAbsentWhenServerFresh: isAttentionPaused,
      });
      const decisionAttention = attentionDecision.decisionAttention;
      const decisionHardAbsent = attentionDecision.decisionHardAbsent;

      lastFaceIndexAtRef.current = decisionSample.receivedAt;
      setDocumentDecisionAttention(decisionAttention);
      setDocumentDecisionSignalAt(decisionSample.receivedAt);
      setDocumentDecisionHardAbsent(decisionHardAbsent);
      setLiveIndexes(indexes);

      const stateKey = `${decisionSample.source}:${decisionHardAbsent}:${decisionAttention}:${indexes.attention}:${documentPlayback.snapshot.pauseReason}:${documentPlayback.isPlaying}`;
      const shouldLog =
        lastDocumentDecisionBridgeStateRef.current !== stateKey ||
        now - lastDocumentDecisionBridgeLogAtRef.current >
          DOCUMENT_FACE_INDEX_DEBUG_INTERVAL_MS;

      if (shouldLog && isDebugVideoPlayerEnabled()) {
        lastDocumentDecisionBridgeStateRef.current = stateKey;
        lastDocumentDecisionBridgeLogAtRef.current = now;
        console.info(
          `[DocumentPlaybackDebug] Decision bridge: source=${decisionSample.source} decision=${decisionAttention} display=${indexes.attention} hardAbsent=${decisionHardAbsent} rawHardAbsent=${decisionSample.hardAbsent} rawAttention=${decisionSample.rawIndexes.attention} serverAttention=${lastServerDecisionSample?.rawIndexes.attention ?? 'none'} pause=${documentPlayback.snapshot.pauseReason ?? 'none'} playing=${documentPlayback.isPlaying}`,
          {
            displayIndexes: indexes,
            decisionAttention,
            faceIndexStatus: faceIndexStatusRef.current,
            hasSignal: decisionSample.hasSignal,
            hardAbsent: decisionHardAbsent,
            decisionDiagnostics: attentionDecision.diagnostics,
            isTracking,
            playbackIsPlaying: documentPlayback.isPlaying,
            playbackPauseReason: documentPlayback.snapshot.pauseReason,
            rawHardAbsent: decisionSample.hardAbsent,
            rawIndexes: decisionSample.rawIndexes,
            receivedAgeMs: now - decisionSample.receivedAt,
            resetDisplayAverage: decisionSample.displayAverageReset,
            serverDecisionAgeMs: Number.isFinite(serverDecisionAgeMs)
              ? serverDecisionAgeMs
              : null,
            serverDecisionAttention:
              lastServerDecisionSample?.rawIndexes.attention ?? null,
            source: decisionSample.source,
          }
        );
      }
    },
    [
      documentPlayback.isPlaying,
      documentPlayback.snapshot.pauseReason,
      documentPlaybackSettings.documentAttentionThreshold,
      isTracking,
    ]
  );

  const handleFaceIndexStatusChange = useCallback(
    (status: 'running' | 'fallback') => {
      faceIndexStatusRef.current = status;
      setFaceIndexStatus(status);
      if (status === 'fallback') {
        lastFaceIndexAtRef.current = null;
        lastServerDecisionSampleRef.current = null;
        setDocumentDecisionAttention(0);
        setDocumentDecisionSignalAt(null);
        setDocumentDecisionHardAbsent(false);
      }
    },
    []
  );

  useEffect(() => {
    if (asset && !linkTitle) {
      setLinkTitle(asset.name);
    }
  }, [asset, linkTitle]);

  const loadAssetLinks = useCallback(async () => {
    if (!asset || assetLinkId) {
      setAssetLinks([]);
      return;
    }

    setIsAssetLinksLoading(true);
    try {
      const response = await API.assetLinks.getByAssetKey(asset.s3Key);
      setAssetLinks(response.data);
    } catch (error) {
      setAssetLinks([]);
    } finally {
      setIsAssetLinksLoading(false);
    }
  }, [asset, assetLinkId]);

  useEffect(() => {
    void loadAssetLinks();
  }, [loadAssetLinks]);

  const handleDeleteAssetLink = useCallback(
    async (id: string) => {
      try {
        await API.assetLinks.delete(id);
        setAssetLinks((current) => current.filter((link) => link.id !== id));
        message.success('Parameterized file link deleted');
      } catch (error) {
        message.error('Unable to delete file link');
      }
    },
    [message]
  );

  useEffect(() => {
    let cancelled = false;
    let metadataTimedOut = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      metadataTimedOut = true;
      controller.abort();
    }, ASSET_LINK_LOAD_TIMEOUT_MS);

    const loadAssetLink = async () => {
      if (!assetLinkId) {
        window.clearTimeout(timeoutId);
        setLinkedAsset(null);
        setActiveAssetLink(null);
        setIsAssetLinkLoading(false);
        return;
      }

      setIsAssetLinkLoading(true);
      try {
        const response = await API.assetLinks.get(assetLinkId, {
          signal: controller.signal,
        });
        if (!cancelled) {
          setActiveAssetLink(response.data);
          setLinkedAsset({
            name: getAiManagerModule1DisplayTitle(
              response.data.s3Key,
              response.data.title ?? response.data.name
            ),
            kind: response.data.kind,
            size: response.data.size ?? '',
            s3Key: response.data.s3Key,
          });
        }
      } catch (error) {
        if (cancelled || (isAbortLikeError(error) && !metadataTimedOut)) {
          return;
        }

        const displayError = metadataTimedOut
          ? new Error('File metadata loading timed out.')
          : error;

        if (!cancelled) {
          setLinkedAsset(null);
          setActiveAssetLink(null);
          if (!manifestAsset) {
            setState({
              status: 'error',
              message: getAssetLinkAccessErrorMessage(displayError),
            });
          }
        }
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelled) {
          setIsAssetLinkLoading(false);
        }
      }
    };

    void loadAssetLink();

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [assetLinkId, manifestAsset]);

  useEffect(() => {
    let revokedUrl: string | null = null;
    let cancelled = false;
    let fileTimedOut = false;
    let timeoutId: number | undefined;
    const controller = new AbortController();

    const load = async () => {
      if (assetLinkId && isAssetLinkLoading && !asset) {
        return;
      }

      if (!asset) {
        setState({ status: 'error', message: 'File not found in Module 1.' });
        return;
      }

      setState({ status: 'loading' });
      setIsPdfReady(false);

      try {
        if (asset.kind === 'video') {
          if (!cancelled) {
            setState({ status: 'ready', url: getStreamUrl(asset.s3Key) });
          }
          return;
        }

        timeoutId = window.setTimeout(() => {
          fileTimedOut = true;
          controller.abort();
        }, FILE_LOAD_TIMEOUT_MS);

        const buffer = await getArrayBuffer(asset.s3Key, controller.signal);
        const blob = new Blob([buffer], {
          type: asset.kind === 'pdf' ? 'application/pdf' : undefined,
        });
        const url = URL.createObjectURL(blob);
        revokedUrl = url;

        if (asset.kind === 'text') {
          const text = new TextDecoder('utf-8').decode(buffer);
          if (!cancelled) setState({ status: 'ready', url, text });
          return;
        }

        if (asset.kind === 'csv') {
          const text = new TextDecoder('utf-8').decode(buffer);
          const workbook = XLSX.read(text, { type: 'string' });
          if (!cancelled) setState({ status: 'ready', url, workbook });
          return;
        }

        if (asset.kind === 'excel') {
          const workbook = XLSX.read(buffer, {
            cellFormula: false,
            cellHTML: false,
            cellNF: false,
          });
          if (!cancelled) setState({ status: 'ready', url, workbook });
          return;
        }

        if (asset.kind === 'document') {
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          const html = DOMPurify.sanitize(result.value, {
            USE_PROFILES: { html: true },
          });
          if (!cancelled) setState({ status: 'ready', url, html });
          return;
        }

        if (asset.kind === 'pdf') {
          if (!cancelled) setState({ status: 'ready', url, pdfData: buffer });
          return;
        }

        if (!cancelled) setState({ status: 'ready', url });
      } catch (error: unknown) {
        if (cancelled || (isAbortLikeError(error) && !fileTimedOut)) {
          return;
        }

        if (!cancelled) {
          setState({
            status: 'error',
            message:
              fileTimedOut || isAbortLikeError(error)
                ? 'File loading timed out. Please try again.'
                : error instanceof Error
                  ? error.message
                  : 'Unable to load file.',
          });
        }
      } finally {
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [asset, assetLinkId, isAssetLinkLoading, linkedAsset]);

  const handlePdfReady = useCallback(() => {
    setIsPdfReady(true);
  }, []);

  useEffect(() => {
    const markActivity = () => {
      lastActivityAtRef.current = Date.now();
    };

    window.addEventListener('click', markActivity);
    window.addEventListener('keydown', markActivity);
    window.addEventListener('scroll', markActivity, true);
    window.addEventListener('mousemove', markActivity);

    return () => {
      window.removeEventListener('click', markActivity);
      window.removeEventListener('keydown', markActivity);
      window.removeEventListener('scroll', markActivity, true);
      window.removeEventListener('mousemove', markActivity);
    };
  }, []);

  useEffect(() => {
    if (assetLinkId && state.status === 'ready') {
      viewStartedAtRef.current = null;
      lastActivityAtRef.current = Date.now();
      setIsTracking(false);
    } else {
      setIsTracking(false);
      setFaceIndexStatus('idle');
      faceIndexStatusRef.current = 'idle';
      lastFaceIndexAtRef.current = null;
      lastServerDecisionSampleRef.current = null;
      setDocumentDecisionAttention(0);
      setDocumentDecisionSignalAt(null);
      setDocumentDecisionHardAbsent(false);
      setLiveIndexes(INITIAL_DOCUMENT_INDEXES);
    }
  }, [assetLinkId, state.status]);

  useEffect(() => {
    if (!assetLinkId || state.status !== 'ready' || !isTracking) {
      return;
    }

    const getViewingDuration = () =>
      viewStartedAtRef.current ? Date.now() - viewStartedAtRef.current : 0;
    const getCurrentIndexes = () => {
      if (
        lastFaceIndexAtRef.current &&
        Date.now() - lastFaceIndexAtRef.current < FACE_INDEX_EVENT_MAX_AGE_MS
      ) {
        return {
          ...liveIndexesRef.current,
          viewingSeconds: Math.round(getViewingDuration() / 1000),
        };
      }

      // No recent real measurement — report the zero state rather than a
      // fabricated fallback, matching the video player's default indexData.
      return {
        ...INITIAL_DOCUMENT_INDEXES,
        viewingSeconds: Math.round(getViewingDuration() / 1000),
      };
    };
    const sendEvent = (
      type: 'open' | 'heartbeat' | 'close' | 'index',
      indexes?: LiveDocumentIndexes
    ) => {
      void API.assetLinks
        .createEvent(assetLinkId, {
          type,
          viewingDuration: getViewingDuration(),
          ...(indexes ? { indexes } : {}),
          metadata: {
            path: window.location.pathname,
            key: asset?.s3Key,
            kind: asset?.kind,
            tracking: isTracking,
            trackingMode:
              lastFaceIndexAtRef.current &&
              Date.now() - lastFaceIndexAtRef.current <
                FACE_INDEX_EVENT_MAX_AGE_MS
                ? 'face-index'
                : 'face-index-unavailable',
            faceIndexStatus: faceIndexStatusRef.current,
            ...(asset?.kind !== 'video'
              ? {
                  documentCompleted: documentCompletedRef.current,
                  documentProgress: documentPlayback.getSnapshot(),
                }
              : {}),
          },
        })
        .catch(() => undefined);
    };

    sendEvent('open');
    const initialIndexes = getCurrentIndexes();
    setLiveIndexes(initialIndexes);
    sendEvent('index', initialIndexes);
    const heartbeatId = window.setInterval(() => sendEvent('heartbeat'), 15000);
    const indexId = window.setInterval(() => {
      const indexes = getCurrentIndexes();
      setLiveIndexes(indexes);
      sendEvent('index', indexes);
    }, 5000);

    return () => {
      window.clearInterval(heartbeatId);
      window.clearInterval(indexId);
      const completedDocumentIndexes = completedDocumentIndexesRef.current;
      const indexes =
        asset?.kind !== 'video' && completedDocumentIndexes
          ? completedDocumentIndexes
          : {
              ...getCurrentIndexes(),
              attention: 0,
              involvement: 0,
              emotionality: 0,
              distraction: 0,
              tiredness: 0,
            };
      setLiveIndexes(indexes);
      sendEvent('close', indexes);
    };
  }, [
    assetLinkId,
    asset?.kind,
    asset?.s3Key,
    documentPlayback.getSnapshot,
    isTracking,
    state.status,
  ]);

  const isDocumentCompleted =
    Boolean(
      assetLinkId && asset?.kind !== 'video' && state.status === 'ready'
    ) &&
    isDocumentProgressComplete(
      documentPlayback.snapshot,
      documentPlaybackSettings.documentRequiredCoverage
    );

  useEffect(() => {
    if (!isDocumentCompleted || documentCompletedRef.current) {
      return;
    }

    documentCompletedRef.current = true;
    completedDocumentSnapshotRef.current = documentPlayback.snapshot;
    completedDocumentIndexesRef.current = liveIndexesRef.current;
    completedDocumentViewingSecondsRef.current = viewStartedAtRef.current
      ? Math.round((Date.now() - viewStartedAtRef.current) / 1000)
      : liveIndexesRef.current.viewingSeconds;
    completedDocumentPauseCountRef.current = documentPauseCountRef.current;
    setIsTracking(false);
    if (documentPlayback.isPlaying) {
      documentPlayback.togglePlaying();
    }
    setDocumentSummaryOpen(true);

    if (assetLinkId && asset?.kind !== 'video') {
      const completedIndexes = liveIndexesRef.current;
      void API.assetLinks
        .createEvent(assetLinkId, {
          type: 'index',
          viewingDuration: viewStartedAtRef.current
            ? Date.now() - viewStartedAtRef.current
            : 0,
          indexes: completedIndexes,
          metadata: {
            path: window.location.pathname,
            key: asset?.s3Key,
            kind: asset?.kind,
            tracking: true,
            trackingMode: 'document-complete',
            faceIndexStatus: faceIndexStatusRef.current,
            documentCompleted: true,
            documentProgress: documentPlayback.snapshot,
          },
        })
        // Unlock the parent playlist only after the backend can see this
        // completion event; otherwise /playlist/:id/item/:next can still be locked.
        .then(() => notifyQualifiedStudyComplete())
        .catch(() => undefined);
    }
  }, [
    asset?.kind,
    asset?.s3Key,
    assetLinkId,
    documentPlayback.snapshot,
    documentPlayback.isPlaying,
    documentPlayback.togglePlaying,
    isDocumentCompleted,
  ]);

  const handleToggleDocumentPlayback = useCallback(() => {
    if (documentPlayback.isPlaying) {
      documentPauseCountRef.current += 1;
      setIsTracking(false);
    } else if (assetLinkId) {
      viewStartedAtRef.current ??= Date.now();
      lastActivityAtRef.current = Date.now();
      setIsTracking(true);
      // Reset to the zero state at the start of a session; real measured
      // values (or zeros) will arrive from DocumentFaceIndexTracker.
      lastFaceIndexAtRef.current = null;
      lastServerDecisionSampleRef.current = null;
      setDocumentDecisionAttention(0);
      setDocumentDecisionSignalAt(null);
      setDocumentDecisionHardAbsent(false);
      setLiveIndexes(INITIAL_DOCUMENT_INDEXES);
    }

    documentPlayback.togglePlaying();
  }, [assetLinkId, documentPlayback.isPlaying, documentPlayback.togglePlaying]);

  const icon =
    asset?.kind === 'video' ? (
      <VideoCameraOutlined />
    ) : asset?.kind === 'excel' || asset?.kind === 'csv' ? (
      <FileExcelOutlined />
    ) : (
      <FileTextOutlined />
    );

  const fitDocumentToViewport = Boolean(
    asset && asset.kind !== 'video' && !isCreateLinkMode
  );
  const isDocumentScaleToggleVisible = Boolean(
    asset &&
      asset.kind !== 'video' &&
      !isCreateLinkMode &&
      documentViewportLayout.isMobilePortrait
  );
  const documentScaleFrameStyle =
    documentNaturalSize.width > 0 && documentNaturalSize.height > 0
      ? {
          height: `${Math.ceil(documentNaturalSize.height * documentScale)}px`,
          width: `${Math.ceil(documentNaturalSize.width * documentScale)}px`,
        }
      : undefined;
  const documentOverlayVisible = Boolean(
    assetLinkId &&
      asset?.kind !== 'video' &&
      state.status === 'ready' &&
      documentPlayback.isPlaying
  );
  const shouldAutoHideDocumentControls = Boolean(
    fitDocumentToViewport &&
      asset?.kind !== 'video' &&
      (documentViewportLayout.isMobilePortrait ||
        documentViewportLayout.isMobileLandscape)
  );
  const documentIndexesShown =
    !shouldAutoHideDocumentControls ||
    documentControlsPinned ||
    documentControlsVisible;
  const documentPlaybackControlsShown =
    !shouldAutoHideDocumentControls || documentControlsVisible;
  const clearDocumentControlsHideTimer = useCallback(() => {
    if (documentControlsHideTimerRef.current !== null) {
      window.clearTimeout(documentControlsHideTimerRef.current);
      documentControlsHideTimerRef.current = null;
    }
  }, []);
  const scheduleDocumentControlsHide = useCallback(() => {
    clearDocumentControlsHideTimer();

    if (!shouldAutoHideDocumentControls) {
      return;
    }

    documentControlsHideTimerRef.current = window.setTimeout(() => {
      setDocumentControlsVisible(false);
      documentControlsHideTimerRef.current = null;
    }, 2800);
  }, [clearDocumentControlsHideTimer, shouldAutoHideDocumentControls]);
  const revealDocumentControls = useCallback(() => {
    if (!shouldAutoHideDocumentControls) {
      return;
    }

    setDocumentControlsVisible(true);
    scheduleDocumentControlsHide();
  }, [scheduleDocumentControlsHide, shouldAutoHideDocumentControls]);
  const handleDocumentStagePointerDown = useCallback(() => {
    revealDocumentControls();
  }, [revealDocumentControls]);

  useEffect(() => {
    if (!shouldAutoHideDocumentControls) {
      clearDocumentControlsHideTimer();
      setDocumentControlsVisible(true);
      return undefined;
    }

    setDocumentControlsVisible(true);
    scheduleDocumentControlsHide();

    return clearDocumentControlsHideTimer;
  }, [
    clearDocumentControlsHideTimer,
    scheduleDocumentControlsHide,
    shouldAutoHideDocumentControls,
  ]);
  const summarySnapshot =
    completedDocumentSnapshotRef.current ?? documentPlayback.snapshot;
  const summaryIndexes = completedDocumentIndexesRef.current ?? liveIndexes;
  const summaryViewsCount = documentCompletedRef.current ? 1 : 0;
  const summaryViewingSeconds =
    completedDocumentViewingSecondsRef.current ??
    summaryIndexes.viewingSeconds ??
    Math.round(summarySnapshot.qualifiedDurationMs / 1000);
  const summaryViewingTimeLabel = formatDocumentResultDuration(
    summaryViewingSeconds
  );
  const summaryPauseClickCount = completedDocumentPauseCountRef.current;
  const formatMetricPercent = (value: number) =>
    `${Math.round(Number(value ?? 0))}%`;
  const documentQualifiedSeconds = Math.round(
    documentPlayback.snapshot.qualifiedDurationMs / 1000
  );
  const documentRequiredSeconds = Math.ceil(
    documentPlayback.snapshot.minimumQualifiedDurationMs / 1000
  );
  const documentQualifiedTimePercent =
    documentPlayback.snapshot.minimumQualifiedDurationMs > 0
      ? Math.min(
          100,
          Math.round(
            (documentPlayback.snapshot.qualifiedDurationMs /
              documentPlayback.snapshot.minimumQualifiedDurationMs) *
              100
          )
        )
      : 0;

  return (
    <Shell $fitToViewport={fitDocumentToViewport} $embedded={isEmbedded}>
      <Content $fitToViewport={fitDocumentToViewport} $embedded={isEmbedded}>
        {!isEmbedded && (
          <>
            <PageHeader>Module 1 Assets</PageHeader>
            <Header>
              <BackLink to={backPath}>
                <ArrowLeftOutlined />
              </BackLink>
              <TitleBlock>
                <Title>{asset?.name ?? 'File viewer'}</Title>
                <Meta>
                  <span>{icon}</span>
                  {asset && <span>{asset.size}</span>}
                  <span>Read-only</span>
                </Meta>
              </TitleBlock>
            </Header>
          </>
        )}
        {!assetLinkId && asset && (
          <HeaderActions>
            <NavLink
              to={`${moduleViewPath}?key=${encodeURIComponent(
                asset.s3Key
              )}&from=${encodeURIComponent(backPath)}&mode=create-link`}
            >
              <Button size="large">Create a file link</Button>
            </NavLink>
          </HeaderActions>
        )}
        <DocumentFaceIndexTracker
          enabled={Boolean(
            assetLinkId &&
              state.status === 'ready' &&
              isTracking &&
              asset?.kind !== 'video'
          )}
          startedAt={viewStartedAtRef.current}
          linkId={assetLinkId ?? ''}
          contentId={asset?.s3Key ?? ''}
          fileType={asset?.kind ?? 'file'}
          viewSessionId={documentPlayback.snapshot.sessionId}
          onIndexes={handleFaceIndexes}
          onStatusChange={handleFaceIndexStatusChange}
        />
        {isCreateLinkMode && !assetLinkId && asset && (
          <LinkCreator
            asset={asset}
            fromParam={fromParam ?? backPath}
            title={linkTitle}
            description={linkDescription}
            generatedLink={generatedLink}
            viewPath={moduleViewPath}
            setTitle={setLinkTitle}
            setDescription={setLinkDescription}
            setGeneratedLink={setGeneratedLink}
            onCreated={(link) => {
              setAssetLinks((current) => [
                link,
                ...current.filter((item) => item.id !== link.id),
              ]);
            }}
            onSuccess={(link) => {
              void navigator.clipboard.writeText(link).catch(() => undefined);
              message.success('Parameterized file link created');
              navigate(
                `${moduleViewPath}?key=${encodeURIComponent(
                  asset.s3Key
                )}&from=${encodeURIComponent(fromParam ?? backPath)}`,
                { replace: true }
              );
            }}
            onError={() => message.error('Unable to create file link')}
          />
        )}
        {!assetLinkId && asset && (
          <AssetLinksList
            links={assetLinks}
            loading={isAssetLinksLoading}
            onDelete={handleDeleteAssetLink}
            fromPath={fromParam ?? backPath}
            viewPath={moduleViewPath}
          />
        )}
        <PlayerStage
          $fitToViewport={fitDocumentToViewport}
          $embedded={isEmbedded}
          onPointerDown={handleDocumentStagePointerDown}
        >
          {assetLinkId &&
            state.status === 'ready' &&
            isQualifiedStudyCompleted && (
              <FileQualifiedBadge
                $fitToViewport={fitDocumentToViewport}
                $embedded={isEmbedded}
              >
                <CheckCircleOutlined />
                Qualified study completed
              </FileQualifiedBadge>
            )}
          {assetLinkId && state.status === 'ready' && (
            <MeasurementBar
              $fitToViewport={fitDocumentToViewport}
              $embedded={isEmbedded}
            >
              <IndexItems
                $fitToViewport={fitDocumentToViewport}
                $embedded={isEmbedded}
                $controlsVisible={documentIndexesShown}
                $controlsPinned={documentControlsPinned}
              >
                <IndexItem>
                  <IndexValueWrapper>
                    <IndexValue>{liveIndexes.attention}%</IndexValue>
                  </IndexValueWrapper>
                  <IndexLabel>Attentiveness</IndexLabel>
                </IndexItem>
                <IndexItem>
                  <IndexValueWrapper>
                    <IndexValue>{liveIndexes.involvement}%</IndexValue>
                  </IndexValueWrapper>
                  <IndexLabel>Involvement</IndexLabel>
                </IndexItem>
                <IndexItem>
                  <IndexValueWrapper>
                    <IndexValue>{liveIndexes.emotionality}%</IndexValue>
                  </IndexValueWrapper>
                  <IndexLabel>Emotionality</IndexLabel>
                </IndexItem>
                <IndexItem>
                  <IndexValueWrapper>
                    <IndexValue>{liveIndexes.distraction}%</IndexValue>
                  </IndexValueWrapper>
                  <IndexLabel>Distraction</IndexLabel>
                </IndexItem>
                <IndexItem>
                  <IndexValueWrapper>
                    <IndexValue>{liveIndexes.tiredness}%</IndexValue>
                  </IndexValueWrapper>
                  <IndexLabel>Tiredness</IndexLabel>
                </IndexItem>
              </IndexItems>
              {asset?.kind !== 'video' ? (
                <PlaybackControls
                  $fitToViewport={fitDocumentToViewport}
                  $embedded={isEmbedded}
                  $controlsVisible={documentPlaybackControlsShown}
                  $controlsPinned={false}
                >
                  <PlaybackProgress>
                    <MeasurementLabel>
                      Qualified time · {documentQualifiedSeconds} /{' '}
                      {documentRequiredSeconds} sec.
                    </MeasurementLabel>
                    <Progress
                      percent={documentQualifiedTimePercent}
                      status={
                        isDocumentProgressComplete(
                          documentPlayback.snapshot,
                          documentPlaybackSettings.documentRequiredCoverage
                        )
                          ? 'success'
                          : 'active'
                      }
                      size="small"
                    />
                    {documentPlayback.snapshot.pauseReason && (
                      <MeasurementLabel>
                        Paused: {documentPlayback.snapshot.pauseReason}
                      </MeasurementLabel>
                    )}
                  </PlaybackProgress>
                  <Button
                    size="large"
                    icon={
                      documentPlayback.isPlaying ? (
                        <PauseCircleOutlined />
                      ) : (
                        <PlayCircleOutlined />
                      )
                    }
                    onClick={handleToggleDocumentPlayback}
                    className="play-pause-btn"
                  >
                    <span className="btn-text">
                      {documentPlayback.isPlaying ? 'Pause' : 'Play'}
                    </span>
                  </Button>
                  {isDocumentScaleToggleVisible && (
                    <Button
                      size="large"
                      icon={
                        documentScaleMode === 'fit' ? (
                          <ZoomInOutlined />
                        ) : (
                          <ZoomOutOutlined />
                        )
                      }
                      onClick={() =>
                        setDocumentScaleMode((current) =>
                          current === 'fit' ? 'read' : 'fit'
                        )
                      }
                      className="scale-btn"
                      title={
                        documentScaleMode === 'fit'
                          ? 'Readable zoom'
                          : 'Fit to screen'
                      }
                    />
                  )}
                  <Button
                    size="large"
                    icon={<PushpinOutlined />}
                    onClick={() =>
                      setDocumentControlsPinned((current) => !current)
                    }
                    className={`pin-btn ${
                      documentControlsPinned ? 'is-active' : ''
                    }`}
                    aria-pressed={documentControlsPinned}
                    title={
                      documentControlsPinned ? 'Unpin controls' : 'Pin controls'
                    }
                  />
                  <Segmented
                    options={DOCUMENT_PLAYBACK_SPEEDS.map((value) => ({
                      label: getDocumentPlaybackSpeedLabel(value),
                      value,
                    }))}
                    value={documentPlayback.speed}
                    onChange={(value) =>
                      documentPlayback.setSpeed(
                        Number(value) as DocumentPlaybackSpeed
                      )
                    }
                  />
                </PlaybackControls>
              ) : (
                <Button
                  size="large"
                  icon={
                    isTracking ? (
                      <PauseCircleOutlined />
                    ) : (
                      <PlayCircleOutlined />
                    )
                  }
                  onClick={() => setIsTracking((current) => !current)}
                  className="play-pause-btn"
                >
                  <span className="btn-text">
                    {isTracking ? 'Stop' : 'Play'}
                  </span>
                </Button>
              )}
            </MeasurementBar>
          )}
          <Panel $fitToViewport={fitDocumentToViewport} $embedded={isEmbedded}>
            {isAssetLinkLoading && !asset ? (
              <CenterMessage>Loading file...</CenterMessage>
            ) : state.status === 'error' ? (
              <CenterMessage>{state.message}</CenterMessage>
            ) : state.status !== 'ready' ? (
              <CenterMessage>Loading file...</CenterMessage>
            ) : asset?.kind === 'video' ? (
              <Video src={state.url} controls />
            ) : (
              <DocumentStage>
                <DocumentViewport
                  $fitToViewport={fitDocumentToViewport}
                  $embedded={isEmbedded}
                  $qualified={isQualifiedStudyCompleted}
                  ref={documentPlayback.viewportRef}
                  onScroll={documentPlayback.handleScroll}
                >
                  <DocumentScaleFrame style={documentScaleFrameStyle}>
                    <DocumentScaleInner
                      $scale={documentScale}
                      ref={documentContentRef}
                    >
                      {state.pdfData ? (
                        <PdfDocument
                          data={state.pdfData}
                          onReady={handlePdfReady}
                        />
                      ) : state.workbook ? (
                        <WorkbookViewer workbook={state.workbook} />
                      ) : state.html ? (
                        <HtmlViewer
                          $embedded={isEmbedded}
                          dangerouslySetInnerHTML={{ __html: state.html }}
                        />
                      ) : (
                        <TextViewer>{state.text}</TextViewer>
                      )}
                    </DocumentScaleInner>
                  </DocumentScaleFrame>
                </DocumentViewport>
                <DocumentFocusOverlay
                  $visible={documentOverlayVisible}
                  $windowBottomPx={documentPlayback.readingWindowBottomPx}
                />
              </DocumentStage>
            )}
          </Panel>
        </PlayerStage>
        <DocumentResultModal
          open={isDocumentSummaryOpen}
          onCancel={() => setDocumentSummaryOpen(false)}
          footer={null}
        >
          <DocumentResultTitle>Thank you for watching</DocumentResultTitle>
          <DocumentResultListTitle>Stats</DocumentResultListTitle>
          <DocumentResultList>
            <DocumentResultListElement>
              Views count
              <DocumentResultListValue>
                {summaryViewsCount}
              </DocumentResultListValue>
            </DocumentResultListElement>
            <DocumentResultListElement>
              Viewing time
              <DocumentResultListValue>
                {summaryViewingTimeLabel}
              </DocumentResultListValue>
            </DocumentResultListElement>
            <DocumentResultListElement>
              Pause count
              <DocumentResultListValue>
                {summaryPauseClickCount}
              </DocumentResultListValue>
            </DocumentResultListElement>
          </DocumentResultList>
          <DocumentResultListTitle>Metrics</DocumentResultListTitle>
          <DocumentResultList>
            <DocumentResultListElement>
              Attentiveness
              <DocumentResultListValue>
                {formatMetricPercent(summaryIndexes.attention)}
              </DocumentResultListValue>
            </DocumentResultListElement>
            <DocumentResultListElement>
              Involvement
              <DocumentResultListValue>
                {formatMetricPercent(summaryIndexes.involvement)}
              </DocumentResultListValue>
            </DocumentResultListElement>
            <DocumentResultListElement>
              Emotionality
              <DocumentResultListValue>
                {formatMetricPercent(summaryIndexes.emotionality)}
              </DocumentResultListValue>
            </DocumentResultListElement>
            <DocumentResultListElement>
              Distraction
              <DocumentResultListValue>
                {formatMetricPercent(summaryIndexes.distraction)}
              </DocumentResultListValue>
            </DocumentResultListElement>
            <DocumentResultListElement>
              Tiredness
              <DocumentResultListValue>
                {formatMetricPercent(summaryIndexes.tiredness)}
              </DocumentResultListValue>
            </DocumentResultListElement>
          </DocumentResultList>
          <DocumentResultButtons>
            <DocumentResultButton
              type="primary"
              style={{
                color: DarkTheme.COLORS.BLACK._400,
              }}
              onClick={() => setDocumentSummaryOpen(false)}
            >
              Continue review
            </DocumentResultButton>
            {!isEmbedded ? (
              <DocumentResultButton
                type="primary"
                ghost
                style={{
                  color: DarkTheme.COLORS.WHITE._100,
                }}
                onClick={() => navigate(backPath)}
              >
                Back to files
              </DocumentResultButton>
            ) : (
              <DocumentResultButton
                type="primary"
                ghost
                style={{
                  color: DarkTheme.COLORS.WHITE._100,
                }}
                onClick={() => setDocumentSummaryOpen(false)}
              >
                Close
              </DocumentResultButton>
            )}
          </DocumentResultButtons>
        </DocumentResultModal>
      </Content>
    </Shell>
  );
};
